/**
 * EmailTemplateModal Component
 *
 * Modal for generating Mailchimp-worthy email templates from HMC events.
 * Allows users to:
 * - Select 1-2 featured events
 * - Add a personalized message
 * - Configure branding options
 * - Preview the email template
 * - Download as .html file
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  X,
  Mail,
  Download,
  Eye,
  EyeOff,
  Search,
  Link2,
  Smartphone,
  Monitor,
  Youtube,
  FileText,
  Sparkles,
  Send,
  Loader2,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  Trophy,
  Music,
  Calendar,
  ClipboardPaste,
  Trash2,
  Maximize2,
  Minimize2,
  Users,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import type { HMCEvent } from '../hmcEventsStore';
import {
  generateEmailTemplate,
  downloadEmailTemplate,
  getEventImageUrl,
  type EmailTemplateConfig,
} from '../utils/emailTemplateGenerator';
import { isAssignedSuiteEvent } from '../utils/attendanceParser';
import {
  WizardStepIndicator,
  Step2ContactSelection,
  Step3SendMode,
  Step4ReviewSend,
  Step5DeliveryRecipients,
  type SendProgress,
} from './email-wizard';
import type { EPGContactWithOrg } from '../hooks/useEpgContactFilter';
import { getContactPrimaryEmail } from '../hooks/useEpgContactFilter';
import { useBatchEmailSend } from '../hooks/useBatchEmailSend';
import { useSavedInvites } from '../hooks/useSavedInvites';
import { apiClient } from '@shared/services/apiClient';
import { getSetting, setSetting } from '@shared/services/userSettingsService';
import { useEmailTemplates } from '../hooks/useEmailTemplates';
import { TemplateSelector } from './TemplateSelector';
import { FullTemplateSelector } from './FullTemplateSelector';
import { InviteNameInput } from './InviteNameInput';
import { ExportHistoryDropdown } from './ExportHistoryDropdown';
import { GuestListPreview } from '@shared/components/GuestListPreview';
import { CreateActivityModal } from '@shared/components/CreateActivityModal';
import type { FullEmailTemplate, SavedInvite, InviteRecipient } from '../types/emailTemplates';

// Namespace for HMC email defaults in PostgreSQL
const SETTINGS_NAMESPACE = 'hmc-email';

// Share link type for guest URL dropdown
interface ShareLink {
  id: number;
  share_token: string;
  share_url: string;
  label: string | null;
  status: 'active' | 'expired' | 'revoked';
  is_revoked?: boolean;
  event_count: number | null;
}

// Setting keys for PostgreSQL backend (via userSettingsService)
// These are stored in app_settings table with namespace 'hmc-email'
const SETTING_KEYS = {
  personalMessage: 'personal-message',
  senderName: 'sender-name',
  emailSignature: 'email-signature',
  guestLinkUrl: 'guest-link-url',
  emailSubject: 'email-subject',
} as const;

// localStorage keys for recent emails (ephemeral, doesn't need PostgreSQL)
const LOCAL_STORAGE_KEYS = {
  sendToEmails: 'hmc_email_recent_send_to_emails',
} as const;

// Helper to save a setting to PostgreSQL backend
const saveSettingToBackend = async (key: string, value: string): Promise<void> => {
  if (value.trim()) {
    await setSetting(key, value, { namespace: SETTINGS_NAMESPACE });
  }
};

// Helper to load a setting from PostgreSQL backend
const loadSettingFromBackend = async (key: string): Promise<string> => {
  const value = await getSetting<string>(key, {
    namespace: SETTINGS_NAMESPACE,
    defaultValue: ''
  });
  return value || '';
};

// Load recent emails from localStorage (ephemeral, doesn't need PostgreSQL)
const loadRecentEmails = (): string[] => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.sendToEmails);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save recent email to localStorage (keeps last 10)
const saveRecentEmail = (email: string): void => {
  try {
    const emails = loadRecentEmails().filter((e) => e.toLowerCase() !== email.toLowerCase());
    emails.unshift(email);
    localStorage.setItem(LOCAL_STORAGE_KEYS.sendToEmails, JSON.stringify(emails.slice(0, 10)));
  } catch {
    // Ignore storage errors
  }
};

interface EmailTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  events: HMCEvent[];
  selectedEventIds?: Set<number>;
  /** Upload ID for fetching existing share links */
  uploadId?: string;
}

type StylePreset = 'premium' | 'classic' | 'minimal';
type PreviewMode = 'desktop' | 'mobile';

// Clean event name (remove attendance info)
function getCleanEventName(opponent: string | null): string {
  if (!opponent) return 'Event';
  const cleaned = opponent
    .replace(/\s*[-–]\s*\d+\s*(tickets?|tix|seats?).*$/i, '')
    .replace(/\s*\(\d+\s*(tickets?|tix|seats?)\).*$/i, '')
    .trim();
  return cleaned || 'Event';
}

// Format date for display
function formatEventDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// Event category types
type EventCategory = 'sporting' | 'concert' | 'other';

export const EmailTemplateModal: React.FC<EmailTemplateModalProps> = ({
  isOpen,
  onClose,
  events,
  selectedEventIds,
  uploadId,
}) => {
  // Selected events by category (up to 6 total per category, 6 total across all)
  // Each category can expand to use unused slots from other categories
  const [sportingEvent1Id, setSportingEvent1Id] = useState<number | null>(null);
  const [sportingEvent2Id, setSportingEvent2Id] = useState<number | null>(null);
  const [sportingEvent3Id, setSportingEvent3Id] = useState<number | null>(null);
  const [sportingEvent4Id, setSportingEvent4Id] = useState<number | null>(null);
  const [sportingEvent5Id, setSportingEvent5Id] = useState<number | null>(null);
  const [sportingEvent6Id, setSportingEvent6Id] = useState<number | null>(null);
  const [concertEvent1Id, setConcertEvent1Id] = useState<number | null>(null);
  const [concertEvent2Id, setConcertEvent2Id] = useState<number | null>(null);
  const [concertEvent3Id, setConcertEvent3Id] = useState<number | null>(null);
  const [concertEvent4Id, setConcertEvent4Id] = useState<number | null>(null);
  const [concertEvent5Id, setConcertEvent5Id] = useState<number | null>(null);
  const [concertEvent6Id, setConcertEvent6Id] = useState<number | null>(null);
  const [otherEvent1Id, setOtherEvent1Id] = useState<number | null>(null);
  const [otherEvent2Id, setOtherEvent2Id] = useState<number | null>(null);
  const [otherEvent3Id, setOtherEvent3Id] = useState<number | null>(null);
  const [otherEvent4Id, setOtherEvent4Id] = useState<number | null>(null);
  const [otherEvent5Id, setOtherEvent5Id] = useState<number | null>(null);
  const [otherEvent6Id, setOtherEvent6Id] = useState<number | null>(null);

  // Track manually expanded rows for each category (user clicked "Add row")
  const [sportingExpandedRows, setSportingExpandedRows] = useState(0); // 0-2 extra rows (4 extra slots)
  const [concertExpandedRows, setConcertExpandedRows] = useState(0);
  const [otherExpandedRows, setOtherExpandedRows] = useState(0);

  // Legacy aliases for backwards compatibility
  const featuredEvent1Id = sportingEvent1Id;
  const setFeaturedEvent1Id = setSportingEvent1Id;
  const featuredEvent2Id = sportingEvent2Id;
  const setFeaturedEvent2Id = setSportingEvent2Id;

  // Personalization (loaded from PostgreSQL backend on modal open)
  const [personalMessage, setPersonalMessage] = useState('');
  const [senderName, setSenderName] = useState('');
  const [emailSignatureHtml, setEmailSignatureHtml] = useState('');

  // Save as default checkboxes
  const [savePersonalMessageAsDefault, setSavePersonalMessageAsDefault] = useState(false);
  const [saveSenderNameAsDefault, setSaveSenderNameAsDefault] = useState(false);
  const [saveSignatureAsDefault, setSaveSignatureAsDefault] = useState(false);
  const [saveGuestLinkAsDefault, setSaveGuestLinkAsDefault] = useState(false);
  const [saveSubjectAsDefault, setSaveSubjectAsDefault] = useState(false);

  // Options
  const [includeFooter, setIncludeFooter] = useState(true);
  const [includeLogo, setIncludeLogo] = useState(true);

  // Premium options
  const [includeGuestLink, setIncludeGuestLink] = useState(false);
  const [guestLinkUrl, setGuestLinkUrl] = useState('');

  // Guest link URL dropdown state
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [loadingShareLinks, setLoadingShareLinks] = useState(false);
  const [showGuestLinkDropdown, setShowGuestLinkDropdown] = useState(false);
  const [stylePreset, setStylePreset] = useState<StylePreset>('premium');
  const [showArtistDescriptions, setShowArtistDescriptions] = useState(true);
  const [showYouTubeThumbnails, setShowYouTubeThumbnails] = useState(true);

  // UI state
  const isMobile = useIsMobile();
  const [showPreview, setShowPreview] = useState(true);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false); // Mobile fullscreen preview overlay
  const [previewMode, setPreviewMode] = useState<PreviewMode>('desktop');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null); // e.g., 'sporting-1', 'concert-2', 'other-1'

  // Send email state
  const [sendToEmail, setSendToEmail] = useState('');
  const [emailSubject, setEmailSubject] = useState('');

  // Email autocomplete state
  const [recentEmails, setRecentEmails] = useState<string[]>(() => loadRecentEmails());
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [filteredEmails, setFilteredEmails] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [sendError, setSendError] = useState('');

  // Test mode state
  const [testMode, setTestMode] = useState(false);
  const [wizardTestModeEnabled, setWizardTestModeEnabled] = useState(false);
  const [wizardTestEmails, setWizardTestEmails] = useState<string[]>(['']);

  // Wizard state for batch email flow
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [wizardContacts, setWizardContacts] = useState<EPGContactWithOrg[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<number>>(new Set());
  const [wizardSendMode, setWizardSendMode] = useState<'individual' | 'bulk-bcc'>('individual');
  const [wizardBccToEmail, setWizardBccToEmail] = useState('');
  const { progress: sendProgress, sendEmails, resetProgress } = useBatchEmailSend();

  // Saved invites hook for persistence
  const {
    savedInvites,
    activeInvite,
    activeInviteId,
    isSaving: isSavingInvite,
    createInvite,
    updateInvite,
    deleteInvite,
    addExportEntry,
    markAsCompleted,
    markAsIncomplete,
    loadInvite,
    clearActiveInvite,
  } = useSavedInvites();

  // Invite name state (for creating/editing invites)
  const [inviteName, setInviteName] = useState('');

  // Filter state for Step 2 (to save with invite)
  const [filterMonths, setFilterMonths] = useState<string[]>([]);
  const [filterEventTypes, setFilterEventTypes] = useState<string[]>([]);

  // Template management hook
  const {
    personalMessageTemplates,
    signatureTemplates,
    subjectTemplates,
    fullTemplates,
    savePersonalMessageTemplate,
    saveSignatureTemplate,
    saveSubjectTemplate,
    saveFullTemplate,
    deletePersonalMessageTemplate,
    deleteSignatureTemplate,
    deleteSubjectTemplate,
    deleteFullTemplate,
  } = useEmailTemplates();

  // Personal Message Override state (for Step3SendMode)
  const [usePersonalMessageOverride, setUsePersonalMessageOverride] = useState(false);
  const [personalMessageOverride, setPersonalMessageOverride] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  // Invitation tracking toggle (default enabled)
  const [trackInvitation, setTrackInvitation] = useState(true);

  // Guest list preview and Pipedrive activity modal state
  const [showGuestListPreview, setShowGuestListPreview] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [excludedPersonIds, setExcludedPersonIds] = useState<Set<number>>(new Set());

  // Refs
  const signatureDivRef = useRef<HTMLDivElement>(null);
  const personalMessageDivRef = useRef<HTMLDivElement>(null);
  const guestLinkDropdownRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Pre-populate from selected events when modal opens
  useEffect(() => {
    if (isOpen && selectedEventIds && selectedEventIds.size > 0) {
      const selectedIds = Array.from(selectedEventIds);
      setFeaturedEvent1Id(selectedIds[0] || null);
      setFeaturedEvent2Id(selectedIds[1] || null);
    }
  }, [isOpen, selectedEventIds]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowPreview(true);
      setMobilePreviewOpen(false);
      setSearchQuery('');
      setActiveDropdown(null);
      // Reset send email state
      setSendStatus('idle');
      setSendError('');
      setIsSending(false);
      // Reset test mode
      setTestMode(false);
      // Reset dropdowns
      setShowGuestLinkDropdown(false);
      setShowEmailSuggestions(false);
      // Reset fullscreen
      setIsFullscreen(false);
      // Reset wizard state
      setWizardStep(1);
      setWizardContacts([]);
      setSelectedContactIds(new Set());
      setWizardSendMode('individual');
      setWizardBccToEmail('');
      setWizardTestModeEnabled(false);
      setWizardTestEmails(['']);
      resetProgress();
      // Reset personal message override state
      setUsePersonalMessageOverride(false);
      setPersonalMessageOverride('');
      setIsExporting(false);
      setTrackInvitation(true);
      // Reset guest list preview and activity modal
      setShowGuestListPreview(false);
      setShowActivityModal(false);
      setExcludedPersonIds(new Set());
    }
  }, [isOpen, resetProgress]);

  // Fetch share links when modal opens (for Guest Link URL dropdown)
  useEffect(() => {
    if (isOpen && uploadId) {
      const fetchShareLinks = async () => {
        setLoadingShareLinks(true);
        try {
          const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
          const response = await fetch(`${apiBaseUrl}/api/hmc/share-links?upload_id=${encodeURIComponent(uploadId)}`, {
            credentials: 'include',
          });
          if (response.ok) {
            const data = await response.json();
            // Only show active, non-revoked links
            const activeLinks = (data.share_links || []).filter(
              (link: ShareLink) => link.status === 'active' && !link.is_revoked
            );
            setShareLinks(activeLinks);
          }
        } catch (error) {
          console.error('[EmailTemplateModal] Failed to fetch share links:', error);
        } finally {
          setLoadingShareLinks(false);
        }
      };
      fetchShareLinks();
    }
  }, [isOpen, uploadId]);

  // Load default values from PostgreSQL backend when modal opens
  // Uses userSettingsService which falls back to localStorage if not authenticated
  useEffect(() => {
    if (isOpen) {
      const loadDefaults = async () => {
        try {
          // Load all saved defaults from PostgreSQL backend in parallel
          const [
            savedPersonalMessage,
            savedSenderName,
            savedSignature,
            savedGuestLinkUrl,
            savedEmailSubject,
          ] = await Promise.all([
            loadSettingFromBackend(SETTING_KEYS.personalMessage),
            loadSettingFromBackend(SETTING_KEYS.senderName),
            loadSettingFromBackend(SETTING_KEYS.emailSignature),
            loadSettingFromBackend(SETTING_KEYS.guestLinkUrl),
            loadSettingFromBackend(SETTING_KEYS.emailSubject),
          ]);

          // Update state with saved values
          if (savedPersonalMessage) setPersonalMessage(savedPersonalMessage);
          if (savedSenderName) setSenderName(savedSenderName);
          if (savedSignature) setEmailSignatureHtml(savedSignature);
          if (savedGuestLinkUrl) setGuestLinkUrl(savedGuestLinkUrl);
          if (savedEmailSubject) setEmailSubject(savedEmailSubject);

          // Initialize contentEditable divs with saved default values (may contain HTML)
          // Use setTimeout to ensure refs are ready after render
          setTimeout(() => {
            if (personalMessageDivRef.current && savedPersonalMessage) {
              personalMessageDivRef.current.innerHTML = savedPersonalMessage;
            }
            if (signatureDivRef.current && savedSignature) {
              signatureDivRef.current.innerHTML = savedSignature;
            }
          }, 0);
        } catch (error) {
          console.error('[EmailTemplateModal] Failed to load defaults from backend:', error);
        }
      };
      loadDefaults();
    }
  }, [isOpen]); // Only run when modal opens

  // Save defaults immediately when checkboxes are checked (don't wait for email send)
  // These save to PostgreSQL backend via userSettingsService
  useEffect(() => {
    if (savePersonalMessageAsDefault && personalMessage) {
      saveSettingToBackend(SETTING_KEYS.personalMessage, personalMessage);
    }
  }, [savePersonalMessageAsDefault, personalMessage]);

  useEffect(() => {
    if (saveSenderNameAsDefault && senderName) {
      saveSettingToBackend(SETTING_KEYS.senderName, senderName);
    }
  }, [saveSenderNameAsDefault, senderName]);

  useEffect(() => {
    if (saveSignatureAsDefault && emailSignatureHtml) {
      saveSettingToBackend(SETTING_KEYS.emailSignature, emailSignatureHtml);
    }
  }, [saveSignatureAsDefault, emailSignatureHtml]);

  useEffect(() => {
    if (saveGuestLinkAsDefault && guestLinkUrl) {
      saveSettingToBackend(SETTING_KEYS.guestLinkUrl, guestLinkUrl);
    }
  }, [saveGuestLinkAsDefault, guestLinkUrl]);

  useEffect(() => {
    if (saveSubjectAsDefault && emailSubject) {
      saveSettingToBackend(SETTING_KEYS.emailSubject, emailSubject);
    }
  }, [saveSubjectAsDefault, emailSubject]);

  // Handle email input change with autocomplete filtering
  const handleEmailInputChange = useCallback((value: string) => {
    setSendToEmail(value);
    if (sendStatus !== 'idle') {
      setSendStatus('idle');
      setSendError('');
    }
    // Filter recent emails based on input
    if (value.trim()) {
      const filtered = recentEmails.filter((email) =>
        email.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredEmails(filtered);
      setShowEmailSuggestions(filtered.length > 0);
    } else {
      setFilteredEmails(recentEmails);
      setShowEmailSuggestions(recentEmails.length > 0);
    }
  }, [recentEmails, sendStatus]);

  // Handle email suggestion selection
  const handleSelectEmailSuggestion = useCallback((email: string) => {
    setSendToEmail(email);
    setShowEmailSuggestions(false);
    emailInputRef.current?.focus();
  }, []);

  // Handle guest link selection from dropdown
  const handleSelectGuestLink = useCallback((link: ShareLink) => {
    setGuestLinkUrl(link.share_url);
    setShowGuestLinkDropdown(false);
    setIncludeGuestLink(true);
  }, []);

  // Close guest link dropdown on outside click
  useEffect(() => {
    if (!showGuestLinkDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (guestLinkDropdownRef.current && !guestLinkDropdownRef.current.contains(e.target as Node)) {
        setShowGuestLinkDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showGuestLinkDropdown]);

  // Close email suggestions on outside click
  useEffect(() => {
    if (!showEmailSuggestions) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (emailInputRef.current && !emailInputRef.current.contains(e.target as Node)) {
        setShowEmailSuggestions(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showEmailSuggestions]);

  // Handle test mode toggle - pre-fill form with test data for all 6 event slots
  useEffect(() => {
    if (testMode) {
      // Pre-fill email and sender
      setSendToEmail('e.roland88@gmail.com');
      setSenderName('Evan Roland');
      setIncludeGuestLink(true);
      setGuestLinkUrl('https://eroland.me/hmc-events/guest/30b298259bba1b675b5b67506f6d82c5af78dabe71a7d921b81ecba63cf62455');

      // Find sporting events (Dynamo, Dash, Rockets, etc.)
      // Note: event_type may be 'general' even for Dynamo/Dash games, so also check opponent text
      const sportingEvents = events.filter((e) => {
        const opponentLower = e.opponent?.toLowerCase() || '';
        return (
          e.event_type === 'dynamo' ||
          e.event_type === 'dash' ||
          opponentLower.includes('dynamo') ||
          opponentLower.includes('dash') ||
          opponentLower.includes('rockets') ||
          opponentLower.includes('astros') ||
          opponentLower.includes('texans')
        );
      });
      if (sportingEvents[0]) setSportingEvent1Id(sportingEvents[0].id);
      if (sportingEvents[1]) setSportingEvent2Id(sportingEvents[1].id);

      // Find concert events (has music_genre)
      const concertEvents = events.filter(
        (e) => e.music_genre && e.music_genre !== 'other'
      );
      // Prefer Demi Lovato and Meghan Trainor for concerts if available
      const demiEvent = concertEvents.find((e) => e.opponent?.toLowerCase().includes('demi lovato'));
      const meghanEvent = concertEvents.find((e) => e.opponent?.toLowerCase().includes('meghan trainor'));
      if (demiEvent) {
        setConcertEvent1Id(demiEvent.id);
      } else if (concertEvents[0]) {
        setConcertEvent1Id(concertEvents[0].id);
      }
      if (meghanEvent) {
        setConcertEvent2Id(meghanEvent.id);
      } else if (concertEvents[1] && concertEvents[1].id !== concertEvent1Id) {
        setConcertEvent2Id(concertEvents[1].id);
      }

      // Find other events (not sporting, not concert)
      const sportingIds = new Set(sportingEvents.map(e => e.id));
      const concertIds = new Set(concertEvents.map(e => e.id));
      const otherEvents = events.filter(
        (e) => !sportingIds.has(e.id) && !concertIds.has(e.id)
      );
      if (otherEvents[0]) setOtherEvent1Id(otherEvents[0].id);
      if (otherEvents[1]) setOtherEvent2Id(otherEvents[1].id);

      // Clear email subject so it auto-generates
      setEmailSubject('');
    }
  }, [testMode, events]);

  // Get all selected events by category
  const sportingEvent1 = useMemo(
    () => events.find((e) => e.id === sportingEvent1Id) || null,
    [events, sportingEvent1Id]
  );
  const sportingEvent2 = useMemo(
    () => events.find((e) => e.id === sportingEvent2Id) || null,
    [events, sportingEvent2Id]
  );
  const concertEvent1 = useMemo(
    () => events.find((e) => e.id === concertEvent1Id) || null,
    [events, concertEvent1Id]
  );
  const concertEvent2 = useMemo(
    () => events.find((e) => e.id === concertEvent2Id) || null,
    [events, concertEvent2Id]
  );
  const concertEvent3 = useMemo(
    () => events.find((e) => e.id === concertEvent3Id) || null,
    [events, concertEvent3Id]
  );
  const concertEvent4 = useMemo(
    () => events.find((e) => e.id === concertEvent4Id) || null,
    [events, concertEvent4Id]
  );
  const concertEvent5 = useMemo(
    () => events.find((e) => e.id === concertEvent5Id) || null,
    [events, concertEvent5Id]
  );
  const concertEvent6 = useMemo(
    () => events.find((e) => e.id === concertEvent6Id) || null,
    [events, concertEvent6Id]
  );
  const otherEvent1 = useMemo(
    () => events.find((e) => e.id === otherEvent1Id) || null,
    [events, otherEvent1Id]
  );
  const otherEvent2 = useMemo(
    () => events.find((e) => e.id === otherEvent2Id) || null,
    [events, otherEvent2Id]
  );
  const otherEvent3 = useMemo(
    () => events.find((e) => e.id === otherEvent3Id) || null,
    [events, otherEvent3Id]
  );
  const otherEvent4 = useMemo(
    () => events.find((e) => e.id === otherEvent4Id) || null,
    [events, otherEvent4Id]
  );
  const otherEvent5 = useMemo(
    () => events.find((e) => e.id === otherEvent5Id) || null,
    [events, otherEvent5Id]
  );
  const otherEvent6 = useMemo(
    () => events.find((e) => e.id === otherEvent6Id) || null,
    [events, otherEvent6Id]
  );
  const sportingEvent3 = useMemo(
    () => events.find((e) => e.id === sportingEvent3Id) || null,
    [events, sportingEvent3Id]
  );
  const sportingEvent4 = useMemo(
    () => events.find((e) => e.id === sportingEvent4Id) || null,
    [events, sportingEvent4Id]
  );
  const sportingEvent5 = useMemo(
    () => events.find((e) => e.id === sportingEvent5Id) || null,
    [events, sportingEvent5Id]
  );
  const sportingEvent6 = useMemo(
    () => events.find((e) => e.id === sportingEvent6Id) || null,
    [events, sportingEvent6Id]
  );

  // Legacy aliases for backwards compatibility
  const featuredEvent1 = sportingEvent1;
  const featuredEvent2 = sportingEvent2;

  // All selected event IDs for checking duplicates
  const allSelectedIds = useMemo(() => {
    return new Set([
      sportingEvent1Id, sportingEvent2Id, sportingEvent3Id, sportingEvent4Id, sportingEvent5Id, sportingEvent6Id,
      concertEvent1Id, concertEvent2Id, concertEvent3Id, concertEvent4Id, concertEvent5Id, concertEvent6Id,
      otherEvent1Id, otherEvent2Id, otherEvent3Id, otherEvent4Id, otherEvent5Id, otherEvent6Id
    ].filter((id): id is number => id !== null));
  }, [
    sportingEvent1Id, sportingEvent2Id, sportingEvent3Id, sportingEvent4Id, sportingEvent5Id, sportingEvent6Id,
    concertEvent1Id, concertEvent2Id, concertEvent3Id, concertEvent4Id, concertEvent5Id, concertEvent6Id,
    otherEvent1Id, otherEvent2Id, otherEvent3Id, otherEvent4Id, otherEvent5Id, otherEvent6Id
  ]);

  // Count how many base slots are used in each category (max 2 each)
  const sportingBaseUsed = useMemo(() =>
    [sportingEvent1Id, sportingEvent2Id].filter(Boolean).length,
    [sportingEvent1Id, sportingEvent2Id]
  );
  const concertBaseUsed = useMemo(() =>
    [concertEvent1Id, concertEvent2Id].filter(Boolean).length,
    [concertEvent1Id, concertEvent2Id]
  );
  const otherBaseUsed = useMemo(() =>
    [otherEvent1Id, otherEvent2Id].filter(Boolean).length,
    [otherEvent1Id, otherEvent2Id]
  );

  // Calculate total unused base slots (available for any category to expand into)
  const totalUnusedBaseSlots = useMemo(() => {
    const sportingUnused = 2 - sportingBaseUsed;
    const concertUnused = 2 - concertBaseUsed;
    const otherUnused = 2 - otherBaseUsed;
    return sportingUnused + concertUnused + otherUnused;
  }, [sportingBaseUsed, concertBaseUsed, otherBaseUsed]);

  // Count extra slots used by each category (slots 3-6)
  const sportingExtraUsed = useMemo(() =>
    [sportingEvent3Id, sportingEvent4Id, sportingEvent5Id, sportingEvent6Id].filter(Boolean).length,
    [sportingEvent3Id, sportingEvent4Id, sportingEvent5Id, sportingEvent6Id]
  );
  const concertExtraUsed = useMemo(() =>
    [concertEvent3Id, concertEvent4Id, concertEvent5Id, concertEvent6Id].filter(Boolean).length,
    [concertEvent3Id, concertEvent4Id, concertEvent5Id, concertEvent6Id]
  );
  const otherExtraUsed = useMemo(() =>
    [otherEvent3Id, otherEvent4Id, otherEvent5Id, otherEvent6Id].filter(Boolean).length,
    [otherEvent3Id, otherEvent4Id, otherEvent5Id, otherEvent6Id]
  );

  // Calculate extra slots available for each category
  // A category can expand into slots left empty by other categories
  const extraSportingSlotsAvailable = useMemo(() => {
    // Sporting can use: unused concert base (2) + unused other base (2) - already claimed by others
    const unusedByOthers = (2 - concertBaseUsed) + (2 - otherBaseUsed);
    const claimedByOthers = concertExtraUsed + otherExtraUsed + concertExpandedRows * 2 + otherExpandedRows * 2;
    return Math.max(0, unusedByOthers - claimedByOthers);
  }, [concertBaseUsed, otherBaseUsed, concertExtraUsed, otherExtraUsed, concertExpandedRows, otherExpandedRows]);

  const extraConcertSlotsAvailable = useMemo(() => {
    const unusedByOthers = (2 - sportingBaseUsed) + (2 - otherBaseUsed);
    const claimedByOthers = sportingExtraUsed + otherExtraUsed + sportingExpandedRows * 2 + otherExpandedRows * 2;
    return Math.max(0, unusedByOthers - claimedByOthers);
  }, [sportingBaseUsed, otherBaseUsed, sportingExtraUsed, otherExtraUsed, sportingExpandedRows, otherExpandedRows]);

  const extraOtherSlotsAvailable = useMemo(() => {
    const unusedByOthers = (2 - sportingBaseUsed) + (2 - concertBaseUsed);
    const claimedByOthers = sportingExtraUsed + concertExtraUsed + sportingExpandedRows * 2 + concertExpandedRows * 2;
    return Math.max(0, unusedByOthers - claimedByOthers);
  }, [sportingBaseUsed, concertBaseUsed, sportingExtraUsed, concertExtraUsed, sportingExpandedRows, concertExpandedRows]);

  // How many slots to show for each category (base 2 + expanded rows * 2)
  const sportingSlotsToShow = useMemo(() => {
    const fromSelections = Math.ceil(sportingExtraUsed / 2) * 2; // Round up to pairs
    const fromExpanded = sportingExpandedRows * 2;
    return 2 + Math.max(fromSelections, fromExpanded);
  }, [sportingExtraUsed, sportingExpandedRows]);

  const concertSlotsToShow = useMemo(() => {
    const fromSelections = Math.ceil(concertExtraUsed / 2) * 2;
    const fromExpanded = concertExpandedRows * 2;
    return 2 + Math.max(fromSelections, fromExpanded);
  }, [concertExtraUsed, concertExpandedRows]);

  const otherSlotsToShow = useMemo(() => {
    const fromSelections = Math.ceil(otherExtraUsed / 2) * 2;
    const fromExpanded = otherExpandedRows * 2;
    return 2 + Math.max(fromSelections, fromExpanded);
  }, [otherExtraUsed, otherExpandedRows]);

  // Auto-generate email subject based on selected events
  useEffect(() => {
    if (!emailSubject && featuredEvent1) {
      const event1Name = getCleanEventName(featuredEvent1.opponent);
      const event2Name = featuredEvent2 ? getCleanEventName(featuredEvent2.opponent) : null;
      const subject = event2Name
        ? `Upcoming Events: ${event1Name} & ${event2Name}`
        : `Event Invitation: ${event1Name}`;
      setEmailSubject(subject);
    }
  }, [featuredEvent1, featuredEvent2, emailSubject]);

  // Categorize events for filtered dropdowns
  // Note: event_type from DB may be 'general' even for Dynamo/Dash games,
  // so we also check opponent text for team names
  const sportingEvents = useMemo(() => {
    return events.filter((e) => {
      const opponentLower = e.opponent?.toLowerCase() || '';
      return (
        e.event_type === 'dynamo' ||
        e.event_type === 'dash' ||
        opponentLower.includes('dynamo') ||
        opponentLower.includes('dash') ||
        opponentLower.includes('rockets') ||
        opponentLower.includes('astros') ||
        opponentLower.includes('texans')
      );
    });
  }, [events]);

  const concertEvents = useMemo(() => {
    return events.filter((e) =>
      e.music_genre && e.music_genre !== 'other'
    );
  }, [events]);

  const otherCategoryEvents = useMemo(() => {
    // Events that are neither sporting nor concerts
    const sportingIds = new Set(sportingEvents.map(e => e.id));
    const concertIds = new Set(concertEvents.map(e => e.id));
    return events.filter((e) => !sportingIds.has(e.id) && !concertIds.has(e.id));
  }, [events, sportingEvents, concertEvents]);

  // Calculate available months from all events (for filter dropdown)
  const availableMonths = useMemo(() => {
    const months = new Set<string>();
    events.forEach((e) => {
      const date = new Date(e.event_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.add(monthKey);
    });
    return Array.from(months).sort();
  }, [events]);

  // Filter events for dropdown based on category, month filter, and search query
  const getFilteredEventsForCategory = useCallback((category: 'sporting' | 'concert' | 'other') => {
    let categoryEvents: HMCEvent[];
    switch (category) {
      case 'sporting':
        categoryEvents = sportingEvents;
        break;
      case 'concert':
        categoryEvents = concertEvents;
        break;
      case 'other':
        categoryEvents = otherCategoryEvents;
        break;
    }

    // Apply month filter
    if (filterMonths.length > 0) {
      categoryEvents = categoryEvents.filter((e) => {
        const date = new Date(e.event_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        return filterMonths.includes(monthKey);
      });
    }

    // Apply search filter
    if (!searchQuery.trim()) return categoryEvents;
    const query = searchQuery.toLowerCase();
    return categoryEvents.filter(
      (e) =>
        e.opponent?.toLowerCase().includes(query) ||
        e.venue?.toLowerCase().includes(query) ||
        e.event_date.includes(query)
    );
  }, [sportingEvents, concertEvents, otherCategoryEvents, searchQuery, filterMonths]);

  // Legacy filter for backwards compatibility
  const filteredEvents = useMemo(() => {
    if (!searchQuery.trim()) return events;
    const query = searchQuery.toLowerCase();
    return events.filter(
      (e) =>
        e.opponent?.toLowerCase().includes(query) ||
        e.venue?.toLowerCase().includes(query) ||
        e.event_date.includes(query)
    );
  }, [events, searchQuery]);

  // Extract ticket availability from special_event field (e.g., "10 available")
  const getTicketAvailability = useCallback((event: HMCEvent): string | null => {
    if (!event.special_event) return null;
    const match = event.special_event.match(/(\d+)\s*available/i);
    if (match) {
      return `${match[1]} tickets`;
    }
    return null;
  }, []);

  // Generate email HTML with all events organized by category
  const generatedHtml = useMemo(() => {
    // Collect events by category (each can have up to 6 when others are empty)
    const sportingEventsArr = [sportingEvent1, sportingEvent2, sportingEvent3, sportingEvent4, sportingEvent5, sportingEvent6].filter(Boolean) as HMCEvent[];
    const concertEventsArr = [concertEvent1, concertEvent2, concertEvent3, concertEvent4, concertEvent5, concertEvent6].filter(Boolean) as HMCEvent[];
    const otherEventsArr = [otherEvent1, otherEvent2, otherEvent3, otherEvent4, otherEvent5, otherEvent6].filter(Boolean) as HMCEvent[];

    // All selected events in order for the template
    const allEvents = [...sportingEventsArr, ...concertEventsArr, ...otherEventsArr];
    if (allEvents.length === 0) return '';

    const config: EmailTemplateConfig = {
      events: allEvents,
      personalMessage,
      senderName,
      includeFooter,
      includeLogo,
      // Premium options
      includeGuestLink,
      guestLinkUrl: includeGuestLink ? guestLinkUrl : '',
      stylePreset,
      showArtistDescriptions,
      showYouTubeThumbnails,
      // New: category groupings for section headers
      eventCategories: {
        sporting: sportingEventsArr,
        concerts: concertEventsArr,
        other: otherEventsArr,
      },
      // Email signature HTML (preserves formatting from Outlook)
      signatureHtml: emailSignatureHtml,
    };

    return generateEmailTemplate(config);
  }, [
    sportingEvent1,
    sportingEvent2,
    sportingEvent3,
    sportingEvent4,
    sportingEvent5,
    sportingEvent6,
    concertEvent1,
    concertEvent2,
    concertEvent3,
    concertEvent4,
    concertEvent5,
    concertEvent6,
    otherEvent1,
    otherEvent2,
    otherEvent3,
    otherEvent4,
    otherEvent5,
    otherEvent6,
    personalMessage,
    senderName,
    includeFooter,
    includeLogo,
    includeGuestLink,
    guestLinkUrl,
    stylePreset,
    showArtistDescriptions,
    showYouTubeThumbnails,
    emailSignatureHtml,
  ]);

  // Create blob URL for iframe preview
  // Note: blob: URLs DO inherit parent CSP, but we include a meta CSP tag in the HTML
  const previewBlobUrl = useMemo(() => {
    if (!generatedHtml) {
      console.log('[EmailPreview] No generatedHtml, returning empty blob URL');
      return '';
    }
    const blob = new Blob([generatedHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    console.log('[EmailPreview] Created blob URL:', url);
    console.log('[EmailPreview] HTML length:', generatedHtml.length);
    console.log('[EmailPreview] HTML preview (first 500 chars):', generatedHtml.substring(0, 500));
    return url;
  }, [generatedHtml]);

  // Cleanup blob URL when component unmounts or URL changes
  useEffect(() => {
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewBlobUrl]);

  // Handle download
  const handleDownload = useCallback(() => {
    if (!generatedHtml) return;
    downloadEmailTemplate(generatedHtml);
  }, [generatedHtml]);

  // Check if form is valid (at least one event selected)
  const isFormValid = allSelectedIds.size > 0;

  // Handle send email via Resend
  const handleSendEmail = useCallback(async () => {
    if (!generatedHtml || !sendToEmail.trim() || !emailSubject.trim()) return;

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sendToEmail.trim())) {
      setSendError('Please enter a valid email address');
      setSendStatus('error');
      return;
    }

    setIsSending(true);
    setSendStatus('idle');
    setSendError('');

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/hmc/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to_email: sendToEmail.trim(),
          subject: emailSubject.trim(),
          html_body: generatedHtml,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSendStatus('success');
        // Save recent email on success (localStorage only - ephemeral)
        saveRecentEmail(sendToEmail.trim());
        setRecentEmails(loadRecentEmails());
        // Save defaults to PostgreSQL backend if checkboxes are checked
        // (Already saved by useEffect, but re-save to ensure consistency)
        if (savePersonalMessageAsDefault) {
          saveSettingToBackend(SETTING_KEYS.personalMessage, personalMessage);
        }
        if (saveSenderNameAsDefault) {
          saveSettingToBackend(SETTING_KEYS.senderName, senderName);
        }
        if (saveSignatureAsDefault) {
          saveSettingToBackend(SETTING_KEYS.emailSignature, emailSignatureHtml);
        }
        if (saveGuestLinkAsDefault) {
          saveSettingToBackend(SETTING_KEYS.guestLinkUrl, guestLinkUrl);
        }
        if (saveSubjectAsDefault) {
          saveSettingToBackend(SETTING_KEYS.emailSubject, emailSubject);
        }
      } else {
        setSendError(data.message || data.detail || 'Failed to send email');
        setSendStatus('error');
      }
    } catch (error) {
      setSendError(error instanceof Error ? error.message : 'Network error');
      setSendStatus('error');
    } finally {
      setIsSending(false);
    }
  }, [
    generatedHtml,
    sendToEmail,
    emailSubject,
    personalMessage,
    senderName,
    emailSignatureHtml,
    guestLinkUrl,
    savePersonalMessageAsDefault,
    saveSenderNameAsDefault,
    saveSignatureAsDefault,
    saveGuestLinkAsDefault,
    saveSubjectAsDefault,
  ]);

  // Check if can send email
  const canSendEmail = isFormValid && sendToEmail.trim() && emailSubject.trim();

  // Get selected events as array for wizard
  const selectedEventsArray = useMemo(() => {
    return events.filter((e) => allSelectedIds.has(e.id));
  }, [events, allSelectedIds]);

  // Get selected contacts for wizard
  const selectedWizardContacts = useMemo(() => {
    return wizardContacts.filter((c) => selectedContactIds.has(c.person_id));
  }, [wizardContacts, selectedContactIds]);

  // Wizard navigation handlers
  const handleWizardStepClick = useCallback((step: 1 | 2 | 3 | 4 | 5) => {
    // Only allow going back, not forward (must use Next buttons)
    if (step < wizardStep) {
      setWizardStep(step);
    }
  }, [wizardStep]);

  const handleWizardNext = useCallback(() => {
    if (wizardStep < 5) {
      setWizardStep((prev) => (prev + 1) as 1 | 2 | 3 | 4 | 5);
    }
  }, [wizardStep]);

  const handleWizardBack = useCallback(() => {
    if (wizardStep > 1) {
      setWizardStep((prev) => (prev - 1) as 1 | 2 | 3 | 4 | 5);
    }
  }, [wizardStep]);

  // Convert selected contacts to InviteRecipient format
  const getInviteRecipients = useCallback((): InviteRecipient[] => {
    return selectedWizardContacts.map((contact) => ({
      personId: contact.person_id,
      name: contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
      title: contact.title || null,
      organization: contact.organization_name || 'Unknown Organization',
      email: getContactPrimaryEmail(contact) || '',
    }));
  }, [selectedWizardContacts]);

  // Handle invite name change - auto-save/create invite
  const handleInviteNameChange = useCallback(async (name: string) => {
    setInviteName(name);

    if (!name.trim()) {
      // Clear active invite if name is removed
      clearActiveInvite();
      return;
    }

    // Build invite data from current state
    const inviteData = {
      sportingEventIds: [sportingEvent1Id, sportingEvent2Id, sportingEvent3Id, sportingEvent4Id, sportingEvent5Id, sportingEvent6Id].filter((id): id is number => id !== null),
      concertEventIds: [concertEvent1Id, concertEvent2Id, concertEvent3Id, concertEvent4Id, concertEvent5Id, concertEvent6Id].filter((id): id is number => id !== null),
      otherEventIds: [otherEvent1Id, otherEvent2Id, otherEvent3Id, otherEvent4Id, otherEvent5Id, otherEvent6Id].filter((id): id is number => id !== null),
      personalMessage,
      senderName,
      emailSignature: emailSignatureHtml,
      emailSubject,
      includeLogo,
      includeFooter,
      includeGuestLink,
      guestLinkUrl,
      stylePreset,
      showArtistDescriptions,
      showYouTubeThumbnails,
      filterMonths,
      filterEventTypes,
      selectedRecipientIds: Array.from(selectedContactIds),
      removedRecipientIds: Array.from(excludedPersonIds),
      recipients: getInviteRecipients(),
      sendMode: wizardSendMode,
      bccToEmail: wizardBccToEmail,
    };

    try {
      if (activeInviteId) {
        // Update existing invite
        await updateInvite(activeInviteId, { name, ...inviteData });
      } else {
        // Create new invite
        await createInvite(name, inviteData);
      }
    } catch (error) {
      console.error('[EmailTemplateModal] Failed to save invite:', error);
    }
  }, [
    activeInviteId,
    sportingEvent1Id, sportingEvent2Id, sportingEvent3Id, sportingEvent4Id, sportingEvent5Id, sportingEvent6Id,
    concertEvent1Id, concertEvent2Id, concertEvent3Id, concertEvent4Id, concertEvent5Id, concertEvent6Id,
    otherEvent1Id, otherEvent2Id, otherEvent3Id, otherEvent4Id, otherEvent5Id, otherEvent6Id,
    personalMessage, senderName, emailSignatureHtml, emailSubject,
    includeLogo, includeFooter, includeGuestLink, guestLinkUrl, stylePreset,
    showArtistDescriptions, showYouTubeThumbnails, filterMonths, filterEventTypes,
    selectedContactIds, excludedPersonIds, wizardSendMode, wizardBccToEmail,
    createInvite, updateInvite, clearActiveInvite, getInviteRecipients,
  ]);

  // Handle loading a saved invite
  const handleLoadInvite = useCallback((invite: SavedInvite) => {
    setInviteName(invite.name);

    // Restore event selections
    setSportingEvent1Id(invite.sportingEventIds[0] || null);
    setSportingEvent2Id(invite.sportingEventIds[1] || null);
    setSportingEvent3Id(invite.sportingEventIds[2] || null);
    setSportingEvent4Id(invite.sportingEventIds[3] || null);
    setSportingEvent5Id(invite.sportingEventIds[4] || null);
    setSportingEvent6Id(invite.sportingEventIds[5] || null);

    setConcertEvent1Id(invite.concertEventIds[0] || null);
    setConcertEvent2Id(invite.concertEventIds[1] || null);
    setConcertEvent3Id(invite.concertEventIds[2] || null);
    setConcertEvent4Id(invite.concertEventIds[3] || null);
    setConcertEvent5Id(invite.concertEventIds[4] || null);
    setConcertEvent6Id(invite.concertEventIds[5] || null);

    setOtherEvent1Id(invite.otherEventIds[0] || null);
    setOtherEvent2Id(invite.otherEventIds[1] || null);
    setOtherEvent3Id(invite.otherEventIds[2] || null);
    setOtherEvent4Id(invite.otherEventIds[3] || null);
    setOtherEvent5Id(invite.otherEventIds[4] || null);
    setOtherEvent6Id(invite.otherEventIds[5] || null);

    // Restore personalization
    setPersonalMessage(invite.personalMessage);
    if (personalMessageDivRef.current) {
      personalMessageDivRef.current.innerHTML = invite.personalMessage;
    }
    setSenderName(invite.senderName);
    setEmailSignatureHtml(invite.emailSignature);
    if (signatureDivRef.current) {
      signatureDivRef.current.innerHTML = invite.emailSignature;
    }
    setEmailSubject(invite.emailSubject);

    // Restore branding options
    setIncludeLogo(invite.includeLogo);
    setIncludeFooter(invite.includeFooter);

    // Restore premium options
    setIncludeGuestLink(invite.includeGuestLink);
    setGuestLinkUrl(invite.guestLinkUrl);
    setStylePreset(invite.stylePreset);
    setShowArtistDescriptions(invite.showArtistDescriptions);
    setShowYouTubeThumbnails(invite.showYouTubeThumbnails);

    // Restore filter state
    setFilterMonths(invite.filterMonths || []);
    setFilterEventTypes(invite.filterEventTypes || []);

    // Restore recipient selection
    setSelectedContactIds(new Set(invite.selectedRecipientIds || []));

    // Restore guest list exclusions
    setExcludedPersonIds(new Set(invite.removedRecipientIds || []));

    // Restore send options
    setWizardSendMode(invite.sendMode);
    setWizardBccToEmail(invite.bccToEmail || '');

    // Load the invite in the hook
    loadInvite(invite.id);

    // Stay on Step 1 to show all restored selections
    // User can use "View Recipients" button to go to Step 5 if needed
  }, [loadInvite]);

  // Handle deleting a saved invite
  const handleDeleteInvite = useCallback(async (id: string) => {
    try {
      await deleteInvite(id);
      if (activeInviteId === id) {
        setInviteName('');
      }
    } catch (error) {
      console.error('[EmailTemplateModal] Failed to delete invite:', error);
    }
  }, [deleteInvite, activeInviteId]);

  // Handle clearing the active invite (start fresh)
  const handleClearInvite = useCallback(() => {
    clearActiveInvite();
    setInviteName('');
  }, [clearActiveInvite]);

  // Handle marking invite as completed
  const handleMarkAsCompleted = useCallback(async () => {
    if (!activeInviteId) return;

    const deliveredRecipients = getInviteRecipients();
    // Save the email HTML and subject along with recipients
    await markAsCompleted(activeInviteId, deliveredRecipients, generatedHtml, emailSubject);

    // Navigate to Step 5 to show delivery list
    setWizardStep(5);
  }, [activeInviteId, markAsCompleted, getInviteRecipients, generatedHtml, emailSubject]);

  // Handle undoing completion (revert to draft)
  const handleUndoComplete = useCallback(async () => {
    if (!activeInviteId) return;

    await markAsIncomplete(activeInviteId);

    // Navigate back to Step 4 (Review & Send) so user can continue editing
    setWizardStep(4);
  }, [activeInviteId, markAsIncomplete]);

  // Handle export with history tracking
  const handleExportWithHistory = useCallback(async (
    exportFn: () => Promise<void>,
    exportType: 'html' | 'eml' | 'mail-merge',
    filename: string
  ) => {
    await exportFn();

    // Track export in invite history if we have an active invite
    if (activeInviteId) {
      await addExportEntry(activeInviteId, exportType, filename);
    }
  }, [activeInviteId, addExportEntry]);

  // Handle contact selection changes from Step2
  const handleContactSelectionChange = useCallback((ids: Set<number>, contacts: EPGContactWithOrg[]) => {
    setSelectedContactIds(ids);
  }, []);

  // Handle all contacts loaded from Step2
  const handleAllContactsLoaded = useCallback((contacts: EPGContactWithOrg[]) => {
    setWizardContacts(contacts);
  }, []);

  // Handle sending emails from Step4
  const handleSendEmails = useCallback(async () => {
    // In test mode, create fake contacts from test emails
    if (wizardTestModeEnabled) {
      const validTestEmails = wizardTestEmails.filter((email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
      });

      if (validTestEmails.length === 0) return;

      // Create fake contacts from test emails
      const testContacts = validTestEmails.map((email, index) => ({
        person_id: -(index + 1), // Negative IDs for test contacts
        name: `Test User ${index + 1}`,
        first_name: `Test${index + 1}`,
        last_name: 'User',
        email_work: email.trim(),
        email_home: null,
        email_other: null,
        organization_name: 'Test Organization',
        organization_id: -1,
        title: null,
        work_location: null,
        matchesSelectedEventTypes: true,
      })) as unknown as typeof selectedWizardContacts;

      await sendEmails({
        contacts: testContacts,
        subject: `[TEST] ${emailSubject}`,
        htmlBody: generatedHtml,
        sendMode: wizardSendMode,
        bccToEmail: wizardSendMode === 'bulk-bcc' ? validTestEmails[0] : wizardBccToEmail,
      });
      return;
    }

    // Normal mode - send to selected contacts
    if (selectedWizardContacts.length === 0) return;

    await sendEmails({
      contacts: selectedWizardContacts,
      subject: emailSubject,
      htmlBody: generatedHtml,
      sendMode: wizardSendMode,
      bccToEmail: wizardBccToEmail,
    });

    // Track invitations if enabled (non-blocking)
    if (trackInvitation) {
      try {
        const { invitationTrackingApi } = await import('@shared/api/invitationTrackingApi');
        const contacts = selectedWizardContacts;
        const trackingPromises = contacts
          .filter(c => getContactPrimaryEmail(c))
          .map(contact => invitationTrackingApi.createInvitation({
            event_type: 'hmc',
            invitation_type: 'resend',
            recipient_email: getContactPrimaryEmail(contact) || '',
            recipient_name: String(contact.first_name || contact.name || '') || undefined,
            recipient_org: contact.organization_name || undefined,
            subject: emailSubject,
            tracking_metadata: {
              send_mode: wizardSendMode,
              events_count: selectedEventsArray.length,
            },
          }));
        await Promise.allSettled(trackingPromises);
      } catch (err) {
        console.warn('[HMCEmailTemplate] Invitation tracking failed (non-critical):', err);
      }
    }
  }, [selectedWizardContacts, emailSubject, generatedHtml, wizardSendMode, wizardBccToEmail, wizardTestModeEnabled, wizardTestEmails, sendEmails, trackInvitation, selectedEventsArray]);

  // Handle wizard done (close modal)
  const handleWizardDone = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle applying a full template
  const handleApplyFullTemplate = useCallback((
    template: FullEmailTemplate,
    validEventIds: { sporting: number[]; concert: number[]; other: number[] }
  ) => {
    // Apply event selections (only valid ones)
    setSportingEvent1Id(validEventIds.sporting[0] || null);
    setSportingEvent2Id(validEventIds.sporting[1] || null);
    setSportingEvent3Id(validEventIds.sporting[2] || null);
    setSportingEvent4Id(validEventIds.sporting[3] || null);
    setSportingEvent5Id(validEventIds.sporting[4] || null);
    setSportingEvent6Id(validEventIds.sporting[5] || null);

    setConcertEvent1Id(validEventIds.concert[0] || null);
    setConcertEvent2Id(validEventIds.concert[1] || null);
    setConcertEvent3Id(validEventIds.concert[2] || null);
    setConcertEvent4Id(validEventIds.concert[3] || null);
    setConcertEvent5Id(validEventIds.concert[4] || null);
    setConcertEvent6Id(validEventIds.concert[5] || null);

    setOtherEvent1Id(validEventIds.other[0] || null);
    setOtherEvent2Id(validEventIds.other[1] || null);
    setOtherEvent3Id(validEventIds.other[2] || null);
    setOtherEvent4Id(validEventIds.other[3] || null);
    setOtherEvent5Id(validEventIds.other[4] || null);
    setOtherEvent6Id(validEventIds.other[5] || null);

    // Apply personalization
    setPersonalMessage(template.personalMessage);
    if (personalMessageDivRef.current) {
      personalMessageDivRef.current.innerHTML = template.personalMessage;
    }
    setSenderName(template.senderName);
    setEmailSignatureHtml(template.emailSignature);
    if (signatureDivRef.current) {
      signatureDivRef.current.innerHTML = template.emailSignature;
    }
    setEmailSubject(template.emailSubject);

    // Apply branding options
    setIncludeLogo(template.includeLogo);
    setIncludeFooter(template.includeFooter);

    // Apply premium options
    setIncludeGuestLink(template.includeGuestLink);
    setGuestLinkUrl(template.guestLinkUrl);
    setStylePreset(template.stylePreset);
    setShowArtistDescriptions(template.showArtistDescriptions);
    setShowYouTubeThumbnails(template.showYouTubeThumbnails);

    // Apply send mode
    setWizardSendMode(template.sendMode);
  }, []);

  // Get current state for saving as full template
  const currentFullTemplateState = useMemo(() => ({
    sportingEventIds: [sportingEvent1Id, sportingEvent2Id, sportingEvent3Id, sportingEvent4Id, sportingEvent5Id, sportingEvent6Id].filter((id): id is number => id !== null),
    concertEventIds: [concertEvent1Id, concertEvent2Id, concertEvent3Id, concertEvent4Id, concertEvent5Id, concertEvent6Id].filter((id): id is number => id !== null),
    otherEventIds: [otherEvent1Id, otherEvent2Id, otherEvent3Id, otherEvent4Id, otherEvent5Id, otherEvent6Id].filter((id): id is number => id !== null),
    personalMessage,
    senderName,
    emailSignature: emailSignatureHtml,
    emailSubject,
    includeLogo,
    includeFooter,
    includeGuestLink,
    guestLinkUrl,
    stylePreset,
    showArtistDescriptions,
    showYouTubeThumbnails,
    sendMode: wizardSendMode,
  }), [
    sportingEvent1Id, sportingEvent2Id, sportingEvent3Id, sportingEvent4Id, sportingEvent5Id, sportingEvent6Id,
    concertEvent1Id, concertEvent2Id, concertEvent3Id, concertEvent4Id, concertEvent5Id, concertEvent6Id,
    otherEvent1Id, otherEvent2Id, otherEvent3Id, otherEvent4Id, otherEvent5Id, otherEvent6Id,
    personalMessage, senderName, emailSignatureHtml, emailSubject,
    includeLogo, includeFooter, includeGuestLink, guestLinkUrl,
    stylePreset, showArtistDescriptions, showYouTubeThumbnails, wizardSendMode,
  ]);

  // Handle saving a full template
  const handleSaveFullTemplate = useCallback(async (name: string) => {
    await saveFullTemplate({ ...currentFullTemplateState, name } as Omit<FullEmailTemplate, 'id' | 'createdAt' | 'updatedAt'>);
  }, [saveFullTemplate, currentFullTemplateState]);

  // Handle event selection from dropdown (category-based)
  const handleSelectEvent = useCallback(
    (eventId: number, dropdownKey: string) => {
      // dropdownKey format: 'category-number' e.g., 'sporting-1', 'concert-2', 'concert-5'
      const [category, num] = dropdownKey.split('-');
      if (category === 'sporting') {
        switch (num) {
          case '1': setSportingEvent1Id(eventId); break;
          case '2': setSportingEvent2Id(eventId); break;
          case '3': setSportingEvent3Id(eventId); break;
          case '4': setSportingEvent4Id(eventId); break;
          case '5': setSportingEvent5Id(eventId); break;
          case '6': setSportingEvent6Id(eventId); break;
        }
      } else if (category === 'concert') {
        switch (num) {
          case '1': setConcertEvent1Id(eventId); break;
          case '2': setConcertEvent2Id(eventId); break;
          case '3': setConcertEvent3Id(eventId); break;
          case '4': setConcertEvent4Id(eventId); break;
          case '5': setConcertEvent5Id(eventId); break;
          case '6': setConcertEvent6Id(eventId); break;
        }
      } else if (category === 'other') {
        switch (num) {
          case '1': setOtherEvent1Id(eventId); break;
          case '2': setOtherEvent2Id(eventId); break;
          case '3': setOtherEvent3Id(eventId); break;
          case '4': setOtherEvent4Id(eventId); break;
          case '5': setOtherEvent5Id(eventId); break;
          case '6': setOtherEvent6Id(eventId); break;
        }
      }
      setActiveDropdown(null);
      setSearchQuery('');
    },
    []
  );

  // Clear event selection (category-based)
  const handleClearEvent = useCallback((dropdownKey: string) => {
    const [category, num] = dropdownKey.split('-');
    if (category === 'sporting') {
      switch (num) {
        case '1': setSportingEvent1Id(null); break;
        case '2': setSportingEvent2Id(null); break;
        case '3': setSportingEvent3Id(null); break;
        case '4': setSportingEvent4Id(null); break;
        case '5': setSportingEvent5Id(null); break;
        case '6': setSportingEvent6Id(null); break;
      }
    } else if (category === 'concert') {
      switch (num) {
        case '1': setConcertEvent1Id(null); break;
        case '2': setConcertEvent2Id(null); break;
        case '3': setConcertEvent3Id(null); break;
        case '4': setConcertEvent4Id(null); break;
        case '5': setConcertEvent5Id(null); break;
        case '6': setConcertEvent6Id(null); break;
      }
    } else if (category === 'other') {
      switch (num) {
        case '1': setOtherEvent1Id(null); break;
        case '2': setOtherEvent2Id(null); break;
        case '3': setOtherEvent3Id(null); break;
        case '4': setOtherEvent4Id(null); break;
        case '5': setOtherEvent5Id(null); break;
        case '6': setOtherEvent6Id(null); break;
      }
    }
  }, []);

  // Handle adding extra rows for a category
  const handleAddRow = useCallback((category: 'sporting' | 'concert' | 'other') => {
    if (category === 'sporting') {
      setSportingExpandedRows((prev) => Math.min(prev + 1, 2)); // Max 2 extra rows = 4 extra slots
    } else if (category === 'concert') {
      setConcertExpandedRows((prev) => Math.min(prev + 1, 2));
    } else if (category === 'other') {
      setOtherExpandedRows((prev) => Math.min(prev + 1, 2));
    }
  }, []);

  // Handle signature paste - preserves all HTML formatting from Outlook
  const handleSignaturePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;

    // Try to get HTML content first (preserves formatting)
    let html = clipboardData.getData('text/html');

    if (html) {
      // Clean the HTML but preserve important formatting
      // Remove Word/Outlook-specific meta tags but keep styles
      html = html
        // Remove Word/Outlook namespace declarations
        .replace(/<\?xml[^>]*>/gi, '')
        .replace(/<!--\[if[^\]]*\]>[\s\S]*?<!\[endif\]-->/gi, '')
        .replace(/<!--.*?-->/g, '')
        // Remove Word-specific tags but keep content
        .replace(/<o:[^>]*>[\s\S]*?<\/o:[^>]*>/gi, '')
        .replace(/<w:[^>]*>[\s\S]*?<\/w:[^>]*>/gi, '')
        // Clean up excessive whitespace
        .replace(/\s+/g, ' ')
        .trim();

      // Extract just the body content if present
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      if (bodyMatch) {
        html = bodyMatch[1];
      }

      setEmailSignatureHtml(html);

      // Update the contentEditable div
      if (signatureDivRef.current) {
        signatureDivRef.current.innerHTML = html;
      }
    } else {
      // Fallback to plain text
      const text = clipboardData.getData('text/plain');
      const formattedHtml = text.replace(/\n/g, '<br>');
      setEmailSignatureHtml(formattedHtml);

      if (signatureDivRef.current) {
        signatureDivRef.current.innerHTML = formattedHtml;
      }
    }
  }, []);

  // Handle signature content change (for manual edits)
  const handleSignatureInput = useCallback(() => {
    if (signatureDivRef.current) {
      setEmailSignatureHtml(signatureDivRef.current.innerHTML);
    }
  }, []);

  // Clear signature
  const handleClearSignature = useCallback(() => {
    setEmailSignatureHtml('');
    if (signatureDivRef.current) {
      signatureDivRef.current.innerHTML = '';
    }
  }, []);

  // Handle personal message paste - preserves HTML formatting from Outlook/Word
  const handlePersonalMessagePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;

    // Try to get HTML content first
    let html = clipboardData.getData('text/html');

    if (html) {
      // Clean up Outlook/Word namespace declarations while preserving styling
      html = html
        .replace(/<\?xml[^>]*>/gi, '')
        .replace(/<!--\[if[^\]]*\]>[\s\S]*?<!\[endif\]-->/gi, '')
        .replace(/<!--.*?-->/g, '')
        .replace(/<o:[^>]*>[\s\S]*?<\/o:[^>]*>/gi, '')
        .replace(/<w:[^>]*>[\s\S]*?<\/w:[^>]*>/gi, '')
        .replace(/\s+/g, ' ')
        .trim();

      // Extract just the body content if present
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      if (bodyMatch) {
        html = bodyMatch[1];
      }

      setPersonalMessage(html);

      // Update the contentEditable div
      if (personalMessageDivRef.current) {
        personalMessageDivRef.current.innerHTML = html;
      }
    } else {
      // Fallback to plain text
      const text = clipboardData.getData('text/plain');
      const formattedHtml = text.replace(/\n/g, '<br>');
      setPersonalMessage(formattedHtml);

      if (personalMessageDivRef.current) {
        personalMessageDivRef.current.innerHTML = formattedHtml;
      }
    }
  }, []);

  // Handle personal message content change (for manual edits)
  const handlePersonalMessageInput = useCallback(() => {
    if (personalMessageDivRef.current) {
      setPersonalMessage(personalMessageDivRef.current.innerHTML);
    }
  }, []);

  // Clear personal message
  const handleClearPersonalMessage = useCallback(() => {
    setPersonalMessage('');
    if (personalMessageDivRef.current) {
      personalMessageDivRef.current.innerHTML = '';
    }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!activeDropdown) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.event-dropdown')) {
        setActiveDropdown(null);
        setSearchQuery('');
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  // Export handler: Download Outlook Draft (.eml file)
  const handleDownloadOutlookDraft = useCallback(async () => {
    if (!generatedHtml) return;
    setIsExporting(true);
    try {
      // Use apiClient for proper authentication (HttpOnly cookies via withCredentials)
      const response = await apiClient.post('/exports/outlook-draft', {
        to_email: wizardBccToEmail,
        bcc_emails: selectedWizardContacts.map(c => getContactPrimaryEmail(c)).filter(Boolean),
        subject: emailSubject,
        html_body: generatedHtml
      }, {
        responseType: 'blob'
      });

      // Download the EML file
      const blob = new Blob([response.data], { type: 'message/rfc822' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'USI-Suite-Invite.eml';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[EmailTemplateModal] Outlook draft export failed:', error);
      // Could show error toast here
    } finally {
      setIsExporting(false);
    }
  }, [generatedHtml, wizardBccToEmail, selectedWizardContacts, emailSubject]);

  // Export handler: Download Word Mail Merge Pack (.zip file)
  const handleDownloadMailMergePack = useCallback(async () => {
    if (!generatedHtml) return;
    setIsExporting(true);
    try {
      // Use apiClient for proper authentication (HttpOnly cookies via withCredentials)
      // EPGContact has 'name' (full name), not separate first_name/last_name fields
      // Pass full_name to backend for intelligent parsing (handles "John Smith Jr., CPA" -> "John")
      const response = await apiClient.post('/exports/mail-merge', {
        recipients: selectedWizardContacts.map(c => {
          // Parse first name from full name (simple split on first space)
          const nameParts = (c.name || '').trim().split(/\s+/);
          const firstName = nameParts[0] || '';
          const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

          return {
            email: getContactPrimaryEmail(c),
            first_name: firstName,
            last_name: lastName,
            full_name: c.name || '',  // Pass full name for backend's intelligent parsing
            company: c.organization_name
          };
        }).filter(r => r.email),
        subject: emailSubject,
        html_body: generatedHtml,
        personal_message_override: usePersonalMessageOverride ? personalMessageOverride : null
      }, {
        responseType: 'blob'
      });

      // Download the ZIP file
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'USI-Outlook-Draft-Generator.zip';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[EmailTemplateModal] Mail merge export failed:', error);
      // Could show error toast here
    } finally {
      setIsExporting(false);
    }
  }, [generatedHtml, selectedWizardContacts, emailSubject, usePersonalMessageOverride, personalMessageOverride]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal content */}
      <div
        className={`relative bg-dark-surface border border-dark-border/30 rounded-xl w-full overflow-hidden shadow-2xl flex flex-col transition-all duration-200 ${
          isFullscreen
            ? 'max-w-none max-h-none h-full rounded-none'
            : 'max-w-7xl max-h-[90vh]'
        }`}
        data-testid="email-template-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border/30 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Email Template Generator</h2>
              <p className="text-sm text-dark-muted">Create a professional email with featured events</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Fullscreen toggle */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-dark-muted hover:text-white hover:bg-dark-border rounded-lg transition-all duration-150"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              data-testid="email-template-fullscreen-button"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-dark-muted hover:text-white hover:bg-dark-border rounded-lg transition-all duration-150"
              data-testid="email-template-close-button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Wizard Step Indicator - shown when in wizard mode (step > 1) */}
        {wizardStep > 1 && (
          <div className="px-2 sm:px-6 py-2 sm:py-3 bg-dark-bg border-b border-dark-border/30">
            <WizardStepIndicator
              currentStep={Math.min(wizardStep, 4) as 1 | 2 | 3 | 4}
              onStepClick={(step) => handleWizardStepClick(step)}
              onPrevious={handleWizardBack}
              onNext={handleWizardNext}
              canGoNext={
                wizardStep === 2 ? selectedContactIds.size > 0 :
                wizardStep === 3 ? (
                  wizardTestModeEnabled
                    ? wizardTestEmails.some((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
                    : (wizardSendMode === 'individual' || wizardBccToEmail.trim() !== '')
                ) :
                false
              }
            />
          </div>
        )}

        {/* Guest List Preview Toggle - shown when contacts are selected in wizard mode */}
        {wizardStep > 1 && selectedWizardContacts.length > 0 && (
          <div className="px-4 py-2 border-b border-dark-border/30 flex items-center gap-2">
            <button
              onClick={() => setShowGuestListPreview(!showGuestListPreview)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-surface border border-dark-border rounded-lg hover:bg-dark-card text-dark-text transition-colors"
            >
              <Users className="w-3.5 h-3.5" />
              Guest List ({selectedWizardContacts.length - excludedPersonIds.size}/{selectedWizardContacts.length})
            </button>
          </div>
        )}

        {/* Guest List Preview Panel */}
        {showGuestListPreview && selectedWizardContacts.length > 0 && (
          <div className="border-b border-dark-border p-3">
            <GuestListPreview
              guests={selectedWizardContacts.map(c => ({
                personId: c.person_id,
                name: c.name || '',
                email: getContactPrimaryEmail(c) || '',
                organization: c.organization_name || c.organization || '',
                title: c.title || null,
              }))}
              excludedIds={excludedPersonIds}
              onExcludedChange={setExcludedPersonIds}
              eventName={selectedEventsArray.map(e => getCleanEventName(e.opponent)).join(', ')}
              emailSubject={emailSubject}
              emailHtml={generatedHtml || undefined}
              showPipedriveButton={true}
              onSendToPipedrive={() => setShowActivityModal(true)}
            />
          </div>
        )}

        {/* Step 2: Contact Selection - wrapper ensures consistent height */}
        {wizardStep === 2 && (
          <div className="flex-1 overflow-hidden min-h-[500px]">
            <Step2ContactSelection
              selectedEvents={selectedEventsArray}
              selectedContactIds={selectedContactIds}
              onSelectionChange={handleContactSelectionChange}
              allContacts={wizardContacts}
              onAllContactsLoaded={handleAllContactsLoaded}
              onBack={handleWizardBack}
              onNext={handleWizardNext}
            />
          </div>
        )}

        {/* Step 3: Send Mode Selection - wrapper ensures consistent height */}
        {wizardStep === 3 && (
          <div className="flex-1 overflow-hidden min-h-[500px]">
            <Step3SendMode
              recipientCount={selectedWizardContacts.length}
              sendMode={wizardSendMode}
              onSendModeChange={setWizardSendMode}
              bccToEmail={wizardBccToEmail}
              onBccToEmailChange={setWizardBccToEmail}
              testModeEnabled={wizardTestModeEnabled}
              onTestModeChange={setWizardTestModeEnabled}
              testEmails={wizardTestEmails}
              onTestEmailsChange={setWizardTestEmails}
              personalMessageOverride={personalMessageOverride}
              onPersonalMessageOverrideChange={setPersonalMessageOverride}
              usePersonalMessageOverride={usePersonalMessageOverride}
              onUsePersonalMessageOverrideChange={setUsePersonalMessageOverride}
              onDownloadOutlookDraft={handleDownloadOutlookDraft}
              onDownloadMailMergePack={handleDownloadMailMergePack}
              isExporting={isExporting}
              hasInviteName={!!inviteName.trim()}
              isCompleted={activeInvite?.isCompleted}
              onMarkAsCompleted={handleMarkAsCompleted}
              onBack={handleWizardBack}
              onNext={handleWizardNext}
            />
          </div>
        )}

        {/* Step 4: Review & Send - wrapper ensures consistent height */}
        {wizardStep === 4 && (
          <div className="flex-1 overflow-hidden min-h-[500px]">
            <Step4ReviewSend
              selectedEvents={selectedEventsArray}
              selectedContacts={selectedWizardContacts}
              sendMode={wizardSendMode}
              bccToEmail={wizardBccToEmail}
              emailSubject={emailSubject}
              sendProgress={sendProgress}
              testModeEnabled={wizardTestModeEnabled}
              testEmails={wizardTestEmails}
              trackInvitation={trackInvitation}
              onTrackInvitationChange={setTrackInvitation}
              onSend={handleSendEmails}
              onBack={handleWizardBack}
              onDone={handleWizardDone}
            />
          </div>
        )}

        {/* Step 5: Delivery Recipients - shown when invite is marked as completed */}
        {wizardStep === 5 && activeInvite?.isCompleted && (
          <div className="flex-1 overflow-hidden min-h-[500px]">
            <Step5DeliveryRecipients
              recipients={activeInvite.deliveredRecipients || []}
              inviteName={activeInvite.name}
              completedAt={activeInvite.completedAt || new Date().toISOString()}
              onBack={() => setWizardStep(4)}
              onDone={onClose}
              onUndoComplete={handleUndoComplete}
              isUndoing={isSavingInvite}
              sentEmailHtml={activeInvite.sentEmailHtml}
              sentEmailSubject={activeInvite.sentEmailSubject}
              guestLinkUrl={activeInvite.guestLinkUrl}
            />
          </div>
        )}

        {/* Content - split view when preview is active (Step 1 only) */}
        {/* On mobile: form takes full width, preview is a separate fullscreen overlay */}
        {/* On desktop: side-by-side split view */}
        {wizardStep === 1 && (
        <div className={`flex-1 overflow-hidden flex ${!isMobile && showPreview ? 'flex-row' : 'flex-col'}`}>
          {/* Form section */}
          <div
            className={`overflow-y-auto ${
              !isMobile && showPreview ? 'w-[35%] min-w-[320px] border-r border-dark-border/30' : 'w-full'
            }`}
          >
            <div className="p-6 space-y-6">
              {/* Invite Name Input - Save all selections with a name */}
              <div className="pb-4 border-b border-dark-border/30">
                <InviteNameInput
                  value={inviteName}
                  onChange={handleInviteNameChange}
                  savedInvites={savedInvites}
                  activeInviteId={activeInviteId}
                  onLoadInvite={handleLoadInvite}
                  onDeleteInvite={handleDeleteInvite}
                  onClearInvite={handleClearInvite}
                  isSaving={isSavingInvite}
                />
              </div>

              {/* Full Template Selector - Load/Save entire configurations */}
              <div className="pb-4 border-b border-dark-border/30">
                <FullTemplateSelector
                  templates={fullTemplates}
                  events={events}
                  currentState={currentFullTemplateState}
                  onApply={handleApplyFullTemplate}
                  onSave={handleSaveFullTemplate}
                  onDelete={deleteFullTemplate}
                />
              </div>

              {/* Event Filters - Month and Event Type */}
              <div className="pb-4 border-b border-dark-border/30">
                <h3 className="text-sm font-medium text-white mb-2">Filter Events</h3>
                <div className="flex items-center gap-4 flex-wrap">
                  {/* Month Filter Dropdown */}
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-dark-muted" />
                    <select
                      value={filterMonths.length === 1 ? filterMonths[0] : ''}
                      onChange={(e) => setFilterMonths(e.target.value ? [e.target.value] : [])}
                      className="px-3 py-1.5 bg-dark-surface border border-dark-border/30 rounded-xl text-sm text-white focus:outline-none focus:border-purple-500"
                    >
                      <option value="">All Months</option>
                      {availableMonths.map((month) => {
                        const date = new Date(month + '-01');
                        const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                        return (
                          <option key={month} value={month}>{label}</option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Event Type Chips - Exclusive single-select: click to show ONLY that type, click again to show all */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setFilterEventTypes(prev =>
                        prev.length === 1 && prev[0] === 'sporting' ? [] : ['sporting']
                      )}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-150 ${
                        filterEventTypes.length === 1 && filterEventTypes[0] === 'sporting'
                          ? 'bg-orange-500 border-orange-500 text-white'
                          : filterEventTypes.length === 0
                            ? 'bg-orange-500/20 border-orange-500/50 text-orange-400'
                            : 'bg-dark-bg border-dark-border/30 text-dark-muted'
                      }`}
                    >
                      <Trophy className="w-3.5 h-3.5" />
                      Sporting
                    </button>
                    <button
                      onClick={() => setFilterEventTypes(prev =>
                        prev.length === 1 && prev[0] === 'concert' ? [] : ['concert']
                      )}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-150 ${
                        filterEventTypes.length === 1 && filterEventTypes[0] === 'concert'
                          ? 'bg-purple-500 border-purple-500 text-white'
                          : filterEventTypes.length === 0
                            ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                            : 'bg-dark-bg border-dark-border/30 text-dark-muted'
                      }`}
                    >
                      <Music className="w-3.5 h-3.5" />
                      Concerts
                    </button>
                    <button
                      onClick={() => setFilterEventTypes(prev =>
                        prev.length === 1 && prev[0] === 'other' ? [] : ['other']
                      )}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-150 ${
                        filterEventTypes.length === 1 && filterEventTypes[0] === 'other'
                          ? 'bg-gray-500 border-gray-500 text-white'
                          : filterEventTypes.length === 0
                            ? 'bg-gray-500/20 border-gray-500/50 text-dark-muted'
                            : 'bg-dark-bg border-dark-border/30 text-dark-muted'
                      }`}
                    >
                      <Calendar className="w-3.5 h-3.5" />
                      Other
                    </button>
                  </div>

                  {/* Clear Filters */}
                  {(filterMonths.length > 0 || filterEventTypes.length > 0) && (
                    <button
                      onClick={() => { setFilterMonths([]); setFilterEventTypes([]); }}
                      className="text-xs text-dark-muted hover:text-white underline"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              </div>

              {/* Featured Events - Categorized (up to 6 total) */}
              <div>
                <h3 className="text-sm font-medium text-white mb-2">
                  Featured Events <span className="text-dark-muted">(min 1, max 6)</span>
                </h3>
                <p className="text-xs text-dark-muted mb-4">
                  Select events to feature in your email. Empty categories will be hidden.
                </p>

                {/* Event Category Sections - Only show if has events or has selections AND matches filter */}
                <div className="space-y-4">
                  {/* Sporting Events - Show if has sporting events or has selections AND filter allows */}
                  {(filterEventTypes.length === 0 || filterEventTypes.includes('sporting')) &&
                   (getFilteredEventsForCategory('sporting').length > 0 || sportingEvent1 || sportingEvent2 || sportingEvent3 || sportingEvent4 || sportingEvent5 || sportingEvent6) && (
                  <div className="p-3 bg-dark-bg/50 border border-orange-500/20 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-orange-400" />
                        <span className="text-sm font-medium text-orange-400">Sporting Events</span>
                        <span className="text-xs text-dark-muted">(0-{2 + extraSportingSlotsAvailable})</span>
                      </div>
                      {extraSportingSlotsAvailable > 0 && sportingSlotsToShow < 2 + extraSportingSlotsAvailable && (
                        <span className="text-[10px] text-orange-400/60 italic">
                          {extraSportingSlotsAvailable} extra slot{extraSportingSlotsAvailable > 1 ? 's' : ''} available
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Sporting Event 1 */}
                      <div className="event-dropdown relative">
                        {sportingEvent1 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(sportingEvent1) ? (
                                <img src={getEventImageUrl(sportingEvent1)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-orange-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(sportingEvent1.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('sporting-1')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'sporting-1' ? null : 'sporting-1'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-orange-500/50 transition-all duration-150"
                          >
                            + Add sporting event
                          </button>
                        )}
                        {activeDropdown === 'sporting-1' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search sporting events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-orange-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('sporting').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No sporting events found</div>
                              ) : (
                                getFilteredEventsForCategory('sporting').map((event) => {
                                  const ticketInfo = getTicketAvailability(event);
                                  const isAssigned = isAssignedSuiteEvent(event.opponent);
                                  const isDisabled = allSelectedIds.has(event.id) || isAssigned;
                                  return (
                                    <button key={event.id} onClick={() => !isDisabled && handleSelectEvent(event.id, 'sporting-1')} disabled={isDisabled} className={`w-full flex items-center gap-2 p-2 text-left transition-all duration-150 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-border'}`}>
                                      <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                        {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-orange-500/20" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-xs truncate ${isAssigned ? 'text-dark-muted' : 'text-white'}`}>{getCleanEventName(event.opponent)}</p>
                                        {isAssigned ? (
                                          <p className="text-[10px] text-amber-400">Assigned Suite</p>
                                        ) : ticketInfo ? (
                                          <p className="text-[10px] text-emerald-400">{ticketInfo}</p>
                                        ) : null}
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Sporting Event 2 */}
                      <div className="event-dropdown relative">
                        {sportingEvent2 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(sportingEvent2) ? (
                                <img src={getEventImageUrl(sportingEvent2)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-orange-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(sportingEvent2.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('sporting-2')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'sporting-2' ? null : 'sporting-2'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-orange-500/50 transition-all duration-150"
                          >
                            + Add sporting event
                          </button>
                        )}
                        {activeDropdown === 'sporting-2' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search sporting events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-orange-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('sporting').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No sporting events found</div>
                              ) : (
                                getFilteredEventsForCategory('sporting').map((event) => {
                                  const ticketInfo = getTicketAvailability(event);
                                  const isAssigned = isAssignedSuiteEvent(event.opponent);
                                  const isDisabled = allSelectedIds.has(event.id) || isAssigned;
                                  return (
                                    <button key={event.id} onClick={() => !isDisabled && handleSelectEvent(event.id, 'sporting-2')} disabled={isDisabled} className={`w-full flex items-center gap-2 p-2 text-left transition-all duration-150 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-border'}`}>
                                      <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                        {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-orange-500/20" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-xs truncate ${isAssigned ? 'text-dark-muted' : 'text-white'}`}>{getCleanEventName(event.opponent)}</p>
                                        {isAssigned ? (
                                          <p className="text-[10px] text-amber-400">Assigned Suite</p>
                                        ) : ticketInfo ? (
                                          <p className="text-[10px] text-emerald-400">{ticketInfo}</p>
                                        ) : null}
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Sporting Event 3 - Extra slot */}
                      {(sportingEvent3 || sportingSlotsToShow >= 4) && (
                      <div className="event-dropdown relative">
                        {sportingEvent3 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(sportingEvent3) ? (
                                <img src={getEventImageUrl(sportingEvent3)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-orange-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(sportingEvent3.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('sporting-3')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'sporting-3' ? null : 'sporting-3'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-orange-500/50 transition-all duration-150"
                          >
                            + Add event
                          </button>
                        )}
                        {activeDropdown === 'sporting-3' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search sporting events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-orange-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('sporting').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No sporting events found</div>
                              ) : (
                                getFilteredEventsForCategory('sporting').map((event) => (
                                  <button key={event.id} onClick={() => handleSelectEvent(event.id, 'sporting-3')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                      {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-orange-500/20" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Sporting Event 4 - Extra slot */}
                      {(sportingEvent4 || sportingSlotsToShow >= 4) && (
                      <div className="event-dropdown relative">
                        {sportingEvent4 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(sportingEvent4) ? (
                                <img src={getEventImageUrl(sportingEvent4)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-orange-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(sportingEvent4.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('sporting-4')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'sporting-4' ? null : 'sporting-4'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-orange-500/50 transition-all duration-150"
                          >
                            + Add event
                          </button>
                        )}
                        {activeDropdown === 'sporting-4' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search sporting events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-orange-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('sporting').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No sporting events found</div>
                              ) : (
                                getFilteredEventsForCategory('sporting').map((event) => (
                                  <button key={event.id} onClick={() => handleSelectEvent(event.id, 'sporting-4')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                      {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-orange-500/20" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Sporting Event 5 - Extra slot */}
                      {(sportingEvent5 || sportingSlotsToShow >= 6) && (
                      <div className="event-dropdown relative">
                        {sportingEvent5 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(sportingEvent5) ? (
                                <img src={getEventImageUrl(sportingEvent5)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-orange-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(sportingEvent5.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('sporting-5')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'sporting-5' ? null : 'sporting-5'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-orange-500/50 transition-all duration-150"
                          >
                            + Add event
                          </button>
                        )}
                        {activeDropdown === 'sporting-5' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search sporting events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-orange-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('sporting').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No sporting events found</div>
                              ) : (
                                getFilteredEventsForCategory('sporting').map((event) => (
                                  <button key={event.id} onClick={() => handleSelectEvent(event.id, 'sporting-5')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                      {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-orange-500/20" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Sporting Event 6 - Extra slot */}
                      {(sportingEvent6 || sportingSlotsToShow >= 6) && (
                      <div className="event-dropdown relative">
                        {sportingEvent6 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(sportingEvent6) ? (
                                <img src={getEventImageUrl(sportingEvent6)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-orange-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(sportingEvent6.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('sporting-6')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'sporting-6' ? null : 'sporting-6'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-orange-500/50 transition-all duration-150"
                          >
                            + Add event
                          </button>
                        )}
                        {activeDropdown === 'sporting-6' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search sporting events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-orange-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('sporting').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No sporting events found</div>
                              ) : (
                                getFilteredEventsForCategory('sporting').map((event) => (
                                  <button key={event.id} onClick={() => handleSelectEvent(event.id, 'sporting-6')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                      {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-orange-500/20" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}
                    </div>

                    {/* Add Row Button - Shows when extra slots are available */}
                    {extraSportingSlotsAvailable > 0 && sportingSlotsToShow < 6 && (
                      <button
                        onClick={() => handleAddRow('sporting')}
                        className="mt-2 w-full p-2 bg-orange-500/10 border border-dashed border-orange-500/40 rounded-lg text-xs text-orange-400 hover:bg-orange-500/20 hover:border-orange-500/60 transition-all duration-150 flex items-center justify-center gap-2"
                      >
                        <span>+ Add row ({Math.min(extraSportingSlotsAvailable, 6 - sportingSlotsToShow)} more slot{Math.min(extraSportingSlotsAvailable, 6 - sportingSlotsToShow) > 1 ? 's' : ''} available)</span>
                      </button>
                    )}
                  </div>
                  )}

                  {/* Concert Events - Show if has concert events or has selections AND filter allows */}
                  {/* Concerts can expand to use unused sporting/other slots (up to 6 total) */}
                  {(filterEventTypes.length === 0 || filterEventTypes.includes('concert')) &&
                   (getFilteredEventsForCategory('concert').length > 0 || concertEvent1 || concertEvent2 || concertEvent3 || concertEvent4 || concertEvent5 || concertEvent6) && (
                  <div className="p-3 bg-dark-bg/50 border border-purple-500/20 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Music className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-medium text-purple-400">Concerts</span>
                        <span className="text-xs text-dark-muted">(0-{2 + extraConcertSlotsAvailable})</span>
                      </div>
                      {extraConcertSlotsAvailable > 0 && concertSlotsToShow < 2 + extraConcertSlotsAvailable && (
                        <span className="text-[10px] text-purple-400/60 italic">
                          {extraConcertSlotsAvailable} extra slot{extraConcertSlotsAvailable > 1 ? 's' : ''} available
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Concert Event 1 */}
                      <div className="event-dropdown relative">
                        {concertEvent1 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(concertEvent1) ? (
                                <img src={getEventImageUrl(concertEvent1)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-purple-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(concertEvent1.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('concert-1')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'concert-1' ? null : 'concert-1'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-purple-500/50 transition-all duration-150"
                          >
                            + Add concert
                          </button>
                        )}
                        {activeDropdown === 'concert-1' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search concerts..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-purple-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('concert').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No concerts found</div>
                              ) : (
                                getFilteredEventsForCategory('concert').map((event) => {
                                  const ticketInfo = getTicketAvailability(event);
                                  return (
                                    <button key={event.id} onClick={() => handleSelectEvent(event.id, 'concert-1')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                      <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                        {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-purple-500/20" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                        {ticketInfo && (
                                          <p className="text-[10px] text-emerald-400">{ticketInfo}</p>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Concert Event 2 */}
                      <div className="event-dropdown relative">
                        {concertEvent2 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(concertEvent2) ? (
                                <img src={getEventImageUrl(concertEvent2)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-purple-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(concertEvent2.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('concert-2')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'concert-2' ? null : 'concert-2'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-purple-500/50 transition-all duration-150"
                          >
                            + Add concert
                          </button>
                        )}
                        {activeDropdown === 'concert-2' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search concerts..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-purple-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('concert').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No concerts found</div>
                              ) : (
                                getFilteredEventsForCategory('concert').map((event) => {
                                  const ticketInfo = getTicketAvailability(event);
                                  return (
                                    <button key={event.id} onClick={() => handleSelectEvent(event.id, 'concert-2')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                      <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                        {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-purple-500/20" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                        {ticketInfo && (
                                          <p className="text-[10px] text-emerald-400">{ticketInfo}</p>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Concert Event 3 - Extra slot (shown when sporting/other slots unused) */}
                      {(concertEvent3 || (extraConcertSlotsAvailable >= 1 && concertSlotsToShow >= 3)) && (
                      <div className="event-dropdown relative">
                        {concertEvent3 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-purple-500/30 rounded-lg">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(concertEvent3) ? (
                                <img src={getEventImageUrl(concertEvent3)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-purple-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(concertEvent3.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('concert-3')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'concert-3' ? null : 'concert-3'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-purple-500/30 rounded-lg text-xs text-purple-400/60 hover:border-purple-500/50 transition-all duration-150"
                          >
                            + Extra concert
                          </button>
                        )}
                        {activeDropdown === 'concert-3' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search concerts..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-purple-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('concert').filter(e => !allSelectedIds.has(e.id)).length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No concerts available</div>
                              ) : (
                                getFilteredEventsForCategory('concert').map((event) => {
                                  const ticketInfo = getTicketAvailability(event);
                                  return (
                                    <button key={event.id} onClick={() => handleSelectEvent(event.id, 'concert-3')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                      <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                        {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-purple-500/20" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                        {ticketInfo && <p className="text-[10px] text-emerald-400">{ticketInfo}</p>}
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Concert Event 4 - Extra slot */}
                      {(concertEvent4 || (extraConcertSlotsAvailable >= 2 && concertSlotsToShow >= 4)) && (
                      <div className="event-dropdown relative">
                        {concertEvent4 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-purple-500/30 rounded-lg">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(concertEvent4) ? (
                                <img src={getEventImageUrl(concertEvent4)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-purple-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(concertEvent4.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('concert-4')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'concert-4' ? null : 'concert-4'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-purple-500/30 rounded-lg text-xs text-purple-400/60 hover:border-purple-500/50 transition-all duration-150"
                          >
                            + Extra concert
                          </button>
                        )}
                        {activeDropdown === 'concert-4' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search concerts..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-purple-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('concert').filter(e => !allSelectedIds.has(e.id)).length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No concerts available</div>
                              ) : (
                                getFilteredEventsForCategory('concert').map((event) => {
                                  const ticketInfo = getTicketAvailability(event);
                                  return (
                                    <button key={event.id} onClick={() => handleSelectEvent(event.id, 'concert-4')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                      <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                        {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-purple-500/20" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                        {ticketInfo && <p className="text-[10px] text-emerald-400">{ticketInfo}</p>}
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Concert Event 5 - Extra slot */}
                      {(concertEvent5 || (extraConcertSlotsAvailable >= 3 && concertSlotsToShow >= 5)) && (
                      <div className="event-dropdown relative">
                        {concertEvent5 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-purple-500/30 rounded-lg">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(concertEvent5) ? (
                                <img src={getEventImageUrl(concertEvent5)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-purple-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(concertEvent5.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('concert-5')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'concert-5' ? null : 'concert-5'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-purple-500/30 rounded-lg text-xs text-purple-400/60 hover:border-purple-500/50 transition-all duration-150"
                          >
                            + Extra concert
                          </button>
                        )}
                        {activeDropdown === 'concert-5' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search concerts..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-purple-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('concert').filter(e => !allSelectedIds.has(e.id)).length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No concerts available</div>
                              ) : (
                                getFilteredEventsForCategory('concert').map((event) => {
                                  const ticketInfo = getTicketAvailability(event);
                                  return (
                                    <button key={event.id} onClick={() => handleSelectEvent(event.id, 'concert-5')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                      <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                        {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-purple-500/20" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                        {ticketInfo && <p className="text-[10px] text-emerald-400">{ticketInfo}</p>}
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Concert Event 6 - Extra slot */}
                      {(concertEvent6 || (extraConcertSlotsAvailable >= 4 && concertSlotsToShow >= 6)) && (
                      <div className="event-dropdown relative">
                        {concertEvent6 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-purple-500/30 rounded-lg">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(concertEvent6) ? (
                                <img src={getEventImageUrl(concertEvent6)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-purple-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(concertEvent6.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('concert-6')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'concert-6' ? null : 'concert-6'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-purple-500/30 rounded-lg text-xs text-purple-400/60 hover:border-purple-500/50 transition-all duration-150"
                          >
                            + Extra concert
                          </button>
                        )}
                        {activeDropdown === 'concert-6' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search concerts..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-purple-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('concert').filter(e => !allSelectedIds.has(e.id)).length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No concerts available</div>
                              ) : (
                                getFilteredEventsForCategory('concert').map((event) => {
                                  const ticketInfo = getTicketAvailability(event);
                                  return (
                                    <button key={event.id} onClick={() => handleSelectEvent(event.id, 'concert-6')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                      <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                        {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-purple-500/20" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                        {ticketInfo && <p className="text-[10px] text-emerald-400">{ticketInfo}</p>}
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}
                    </div>

                    {/* Add Row Button - Shows when extra slots are available */}
                    {extraConcertSlotsAvailable > 0 && concertSlotsToShow < 6 && (
                      <button
                        onClick={() => handleAddRow('concert')}
                        className="mt-2 w-full p-2 bg-purple-500/10 border border-dashed border-purple-500/40 rounded-lg text-xs text-purple-400 hover:bg-purple-500/20 hover:border-purple-500/60 transition-all duration-150 flex items-center justify-center gap-2"
                      >
                        <span>+ Add row ({Math.min(extraConcertSlotsAvailable, 6 - concertSlotsToShow)} more slot{Math.min(extraConcertSlotsAvailable, 6 - concertSlotsToShow) > 1 ? 's' : ''} available)</span>
                      </button>
                    )}
                  </div>
                  )}

                  {/* Other Events - Show if has other events or has selections AND filter allows */}
                  {(filterEventTypes.length === 0 || filterEventTypes.includes('other')) &&
                   (getFilteredEventsForCategory('other').length > 0 || otherEvent1 || otherEvent2 || otherEvent3 || otherEvent4 || otherEvent5 || otherEvent6) && (
                  <div className="p-3 bg-dark-bg/50 border border-cyan-500/20 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm font-medium text-cyan-400">Other Events</span>
                        <span className="text-xs text-dark-muted">(0-{2 + extraOtherSlotsAvailable})</span>
                      </div>
                      {extraOtherSlotsAvailable > 0 && otherSlotsToShow < 2 + extraOtherSlotsAvailable && (
                        <span className="text-[10px] text-cyan-400/60 italic">
                          {extraOtherSlotsAvailable} extra slot{extraOtherSlotsAvailable > 1 ? 's' : ''} available
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Other Event 1 */}
                      <div className="event-dropdown relative">
                        {otherEvent1 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(otherEvent1) ? (
                                <img src={getEventImageUrl(otherEvent1)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-cyan-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(otherEvent1.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('other-1')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'other-1' ? null : 'other-1'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-cyan-500/50 transition-all duration-150"
                          >
                            + Add event
                          </button>
                        )}
                        {activeDropdown === 'other-1' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search other events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-cyan-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('other').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No other events found</div>
                              ) : (
                                getFilteredEventsForCategory('other').map((event) => {
                                  const ticketInfo = getTicketAvailability(event);
                                  return (
                                    <button key={event.id} onClick={() => handleSelectEvent(event.id, 'other-1')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                      <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                        {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-cyan-500/20" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                        {ticketInfo && (
                                          <p className="text-[10px] text-emerald-400">{ticketInfo}</p>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Other Event 2 */}
                      <div className="event-dropdown relative">
                        {otherEvent2 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(otherEvent2) ? (
                                <img src={getEventImageUrl(otherEvent2)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-cyan-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(otherEvent2.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('other-2')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'other-2' ? null : 'other-2'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-cyan-500/50 transition-all duration-150"
                          >
                            + Add event
                          </button>
                        )}
                        {activeDropdown === 'other-2' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search other events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-cyan-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('other').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No other events found</div>
                              ) : (
                                getFilteredEventsForCategory('other').map((event) => {
                                  const ticketInfo = getTicketAvailability(event);
                                  return (
                                    <button key={event.id} onClick={() => handleSelectEvent(event.id, 'other-2')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                      <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                        {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-cyan-500/20" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                        {ticketInfo && (
                                          <p className="text-[10px] text-emerald-400">{ticketInfo}</p>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Other Event 3 - Extra slot */}
                      {(otherEvent3 || otherSlotsToShow >= 4) && (
                      <div className="event-dropdown relative">
                        {otherEvent3 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(otherEvent3) ? (
                                <img src={getEventImageUrl(otherEvent3)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-cyan-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(otherEvent3.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('other-3')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'other-3' ? null : 'other-3'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-cyan-500/50 transition-all duration-150"
                          >
                            + Add event
                          </button>
                        )}
                        {activeDropdown === 'other-3' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search other events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-cyan-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('other').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No other events found</div>
                              ) : (
                                getFilteredEventsForCategory('other').map((event) => (
                                  <button key={event.id} onClick={() => handleSelectEvent(event.id, 'other-3')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                      {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-cyan-500/20" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Other Event 4 - Extra slot */}
                      {(otherEvent4 || otherSlotsToShow >= 4) && (
                      <div className="event-dropdown relative">
                        {otherEvent4 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(otherEvent4) ? (
                                <img src={getEventImageUrl(otherEvent4)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-cyan-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(otherEvent4.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('other-4')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'other-4' ? null : 'other-4'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-cyan-500/50 transition-all duration-150"
                          >
                            + Add event
                          </button>
                        )}
                        {activeDropdown === 'other-4' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search other events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-cyan-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('other').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No other events found</div>
                              ) : (
                                getFilteredEventsForCategory('other').map((event) => (
                                  <button key={event.id} onClick={() => handleSelectEvent(event.id, 'other-4')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                      {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-cyan-500/20" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Other Event 5 - Extra slot */}
                      {(otherEvent5 || otherSlotsToShow >= 6) && (
                      <div className="event-dropdown relative">
                        {otherEvent5 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(otherEvent5) ? (
                                <img src={getEventImageUrl(otherEvent5)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-cyan-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(otherEvent5.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('other-5')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'other-5' ? null : 'other-5'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-cyan-500/50 transition-all duration-150"
                          >
                            + Add event
                          </button>
                        )}
                        {activeDropdown === 'other-5' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search other events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-cyan-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('other').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No other events found</div>
                              ) : (
                                getFilteredEventsForCategory('other').map((event) => (
                                  <button key={event.id} onClick={() => handleSelectEvent(event.id, 'other-5')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                      {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-cyan-500/20" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Other Event 6 - Extra slot */}
                      {(otherEvent6 || otherSlotsToShow >= 6) && (
                      <div className="event-dropdown relative">
                        {otherEvent6 ? (
                          <div className="flex items-center gap-2 p-2 bg-dark-bg border border-dark-border/30 rounded-xl">
                            <div className="w-10 h-8 rounded overflow-hidden bg-dark-border shrink-0">
                              {getEventImageUrl(otherEvent6) ? (
                                <img src={getEventImageUrl(otherEvent6)!} alt="" className="w-full h-full object-cover" />
                              ) : <div className="w-full h-full bg-cyan-500/20" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{getCleanEventName(otherEvent6.opponent)}</p>
                            </div>
                            <button onClick={() => handleClearEvent('other-6')} className="p-1 text-dark-muted hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === 'other-6' ? null : 'other-6'); }}
                            className="w-full p-2 bg-dark-bg border border-dashed border-dark-border/30 rounded-lg text-xs text-dark-muted hover:border-cyan-500/50 transition-all duration-150"
                          >
                            + Add event
                          </button>
                        )}
                        {activeDropdown === 'other-6' && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-20 max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-dark-border/30">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-dark-muted" />
                                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search other events..." className="w-full pl-7 pr-2 py-1.5 bg-dark-surface border border-dark-border/30 rounded text-xs text-white placeholder:text-dark-muted focus:outline-none focus:border-cyan-500" autoFocus />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {getFilteredEventsForCategory('other').length === 0 ? (
                                <div className="p-3 text-xs text-dark-muted text-center">No other events found</div>
                              ) : (
                                getFilteredEventsForCategory('other').map((event) => (
                                  <button key={event.id} onClick={() => handleSelectEvent(event.id, 'other-6')} disabled={allSelectedIds.has(event.id)} className={`w-full flex items-center gap-2 p-2 text-left hover:bg-dark-border transition-all duration-150 ${allSelectedIds.has(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <div className="w-8 h-6 rounded overflow-hidden bg-dark-border shrink-0">
                                      {getEventImageUrl(event) ? <img src={getEventImageUrl(event)!} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-cyan-500/20" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-white truncate">{getCleanEventName(event.opponent)}</p>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      )}
                    </div>

                    {/* Add Row Button - Shows when extra slots are available */}
                    {extraOtherSlotsAvailable > 0 && otherSlotsToShow < 6 && (
                      <button
                        onClick={() => handleAddRow('other')}
                        className="mt-2 w-full p-2 bg-cyan-500/10 border border-dashed border-cyan-500/40 rounded-lg text-xs text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-500/60 transition-all duration-150 flex items-center justify-center gap-2"
                      >
                        <span>+ Add row ({Math.min(extraOtherSlotsAvailable, 6 - otherSlotsToShow)} more slot{Math.min(extraOtherSlotsAvailable, 6 - otherSlotsToShow) > 1 ? 's' : ''} available)</span>
                      </button>
                    )}
                  </div>
                  )}
                </div>

                {/* Selected count */}
                <p className="mt-3 text-xs text-dark-muted text-center">
                  {allSelectedIds.size} event{allSelectedIds.size !== 1 ? 's' : ''} selected
                  {allSelectedIds.size === 0 && ' (minimum 1 required)'}
                </p>
              </div>

              {/* Personal Message - Supports HTML formatting */}
              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="flex items-center gap-2 text-sm font-medium text-purple-400">
                    <ClipboardPaste className="w-4 h-4" />
                    Personal Message
                    <span className="text-xs text-dark-muted font-normal">(optional)</span>
                  </h3>
                  <div className="flex items-center gap-2">
                    <TemplateSelector
                      templates={personalMessageTemplates}
                      currentValue={personalMessage}
                      onSelect={(content) => {
                        setPersonalMessage(content);
                        if (personalMessageDivRef.current) {
                          personalMessageDivRef.current.innerHTML = content;
                        }
                      }}
                      onSave={savePersonalMessageTemplate}
                      onDelete={deletePersonalMessageTemplate}
                      placeholder="Choose message..."
                      compact
                    />
                    {personalMessage && (
                      <button
                        onClick={handleClearPersonalMessage}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-all duration-150"
                      >
                        <Trash2 className="w-3 h-3" />
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-dark-muted mb-3">
                  Paste formatted text from Outlook/Word. All formatting, colors, fonts, and links will be preserved.
                </p>
                <div
                  ref={personalMessageDivRef}
                  contentEditable
                  onPaste={handlePersonalMessagePaste}
                  onInput={handlePersonalMessageInput}
                  className={`min-h-[100px] max-h-[200px] overflow-y-auto p-3 bg-dark-surface border-2 border-dashed rounded-lg text-sm focus:outline-none focus:border-purple-500 transition-all duration-150 ${
                    personalMessage ? 'border-purple-500/50' : 'border-dark-border/30'
                  }`}
                  style={{
                    fontFamily: 'inherit',
                    lineHeight: '1.4',
                    color: '#374151',
                  }}
                />
                <div className="mt-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-dark-muted cursor-pointer hover:text-dark-text">
                    <input
                      type="checkbox"
                      checked={savePersonalMessageAsDefault}
                      onChange={(e) => setSavePersonalMessageAsDefault(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-dark-border/30 bg-dark-bg text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                    />
                    Save as default
                  </label>
                  {!personalMessage && (
                    <span className="text-xs text-dark-muted italic">Click and paste formatted text</span>
                  )}
                </div>
              </div>

              {/* Sender Name */}
              <div className="mt-3">
                <label className="block text-xs text-dark-muted mb-1">Your Name (for signature)</label>
                <input
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="e.g., John Smith"
                  className="w-full p-3 bg-dark-bg border border-dark-border/30 rounded-xl text-sm text-white placeholder:text-dark-muted focus:outline-none focus:border-purple-500"
                />
                <label className="flex items-center gap-2 mt-1 text-xs text-dark-muted cursor-pointer hover:text-dark-text">
                  <input
                    type="checkbox"
                    checked={saveSenderNameAsDefault}
                    onChange={(e) => setSaveSenderNameAsDefault(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-dark-border/30 bg-dark-bg text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                  />
                  Save as default
                </label>
              </div>

              {/* Email Signature Paste Area */}
              <div className="mt-4 p-4 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-emerald-400">
                    <ClipboardPaste className="w-4 h-4" />
                    Email Signature
                    <span className="text-xs text-dark-muted font-normal">(optional)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <TemplateSelector
                      templates={signatureTemplates}
                      currentValue={emailSignatureHtml}
                      onSelect={(content) => {
                        setEmailSignatureHtml(content);
                        if (signatureDivRef.current) {
                          signatureDivRef.current.innerHTML = content;
                        }
                      }}
                      onSave={saveSignatureTemplate}
                      onDelete={deleteSignatureTemplate}
                      placeholder="Choose signature..."
                      compact
                    />
                    {emailSignatureHtml && (
                      <button
                        onClick={handleClearSignature}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-all duration-150"
                      >
                        <Trash2 className="w-3 h-3" />
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-dark-muted mb-3">
                  Paste your Outlook email signature here. All formatting, colors, fonts, and images will be preserved.
                </p>
                <div
                  ref={signatureDivRef}
                  contentEditable
                  onPaste={handleSignaturePaste}
                  onInput={handleSignatureInput}
                  className={`min-h-[100px] max-h-[200px] overflow-y-auto p-3 bg-dark-surface border-2 border-dashed rounded-lg text-sm focus:outline-none focus:border-emerald-500 transition-all duration-150 ${
                    emailSignatureHtml ? 'border-emerald-500/50' : 'border-dark-border/30'
                  }`}
                  style={{
                    // Ensure Outlook fonts render correctly
                    fontFamily: 'inherit',
                    lineHeight: '1.4',
                  }}
                />
                {!emailSignatureHtml && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-dark-muted">
                    <span className="px-2 py-0.5 bg-dark-bg rounded text-dark-muted">Ctrl+V</span>
                    <span>to paste your signature from Outlook</span>
                  </div>
                )}
                {emailSignatureHtml && (
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-emerald-400">
                      ✓ Signature added - will appear at the bottom of your email
                    </span>
                    <label className="flex items-center gap-2 text-xs text-dark-muted cursor-pointer hover:text-dark-text">
                      <input
                        type="checkbox"
                        checked={saveSignatureAsDefault}
                        onChange={(e) => setSaveSignatureAsDefault(e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-dark-border/30 bg-dark-bg text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                      />
                      Save as default
                    </label>
                  </div>
                )}
              </div>

              {/* Branding Options */}
              <div>
                <h3 className="text-sm font-medium text-white mb-3">Branding Options</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 bg-dark-bg border border-dark-border/30 rounded-xl cursor-pointer hover:border-dark-border/30 transition-all duration-150">
                    <input
                      type="checkbox"
                      checked={includeLogo}
                      onChange={(e) => setIncludeLogo(e.target.checked)}
                      className="w-4 h-4 rounded border-dark-border/30 bg-dark-bg text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                    />
                    <span className="text-sm text-white">Include USI branding</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-dark-bg border border-dark-border/30 rounded-xl cursor-pointer hover:border-dark-border/30 transition-all duration-150">
                    <input
                      type="checkbox"
                      checked={includeFooter}
                      onChange={(e) => setIncludeFooter(e.target.checked)}
                      className="w-4 h-4 rounded border-dark-border/30 bg-dark-bg text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                    />
                    <span className="text-sm text-white">Include footer contact info</span>
                  </label>
                </div>
              </div>

              {/* Premium Options */}
              <div>
                <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  Premium Options
                </h3>
                <div className="space-y-3">
                  {/* Style Preset */}
                  <div>
                    <label className="block text-xs text-dark-muted mb-2">Style Preset</label>
                    <div className="flex gap-2">
                      {(['premium', 'classic', 'minimal'] as StylePreset[]).map((preset) => (
                        <button
                          key={preset}
                          onClick={() => setStylePreset(preset)}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                            stylePreset === preset
                              ? 'bg-purple-500 text-white'
                              : 'bg-dark-bg border border-dark-border/30 text-dark-text hover:border-dark-border/30'
                          }`}
                        >
                          {preset.charAt(0).toUpperCase() + preset.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* YouTube Thumbnails Toggle */}
                  <label className="flex items-center gap-3 p-3 bg-dark-bg border border-dark-border/30 rounded-xl cursor-pointer hover:border-dark-border/30 transition-all duration-150">
                    <input
                      type="checkbox"
                      checked={showYouTubeThumbnails}
                      onChange={(e) => setShowYouTubeThumbnails(e.target.checked)}
                      className="w-4 h-4 rounded border-dark-border/30 bg-dark-bg text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                    />
                    <Youtube className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-white">Show YouTube thumbnails</span>
                  </label>

                  {/* Artist Descriptions Toggle */}
                  <label className="flex items-center gap-3 p-3 bg-dark-bg border border-dark-border/30 rounded-xl cursor-pointer hover:border-dark-border/30 transition-all duration-150">
                    <input
                      type="checkbox"
                      checked={showArtistDescriptions}
                      onChange={(e) => setShowArtistDescriptions(e.target.checked)}
                      className="w-4 h-4 rounded border-dark-border/30 bg-dark-bg text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                    />
                    <FileText className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-white">Show artist/event descriptions</span>
                  </label>

                  {/* Guest Link Toggle */}
                  <label className="flex items-center gap-3 p-3 bg-dark-bg border border-dark-border/30 rounded-xl cursor-pointer hover:border-dark-border/30 transition-all duration-150">
                    <input
                      type="checkbox"
                      checked={includeGuestLink}
                      onChange={(e) => setIncludeGuestLink(e.target.checked)}
                      className="w-4 h-4 rounded border-dark-border/30 bg-dark-bg text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                    />
                    <Link2 className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-white">Include guest link button</span>
                  </label>

                  {/* Guest Link URL Input (shown when toggle is on) */}
                  {includeGuestLink && (
                    <div className="ml-6 p-3 bg-dark-bg/50 border border-dark-border/30 rounded-xl">
                      <label className="block text-xs text-dark-muted mb-1">Guest Link URL</label>

                      {/* Existing Links Dropdown */}
                      {shareLinks.length > 0 && (
                        <div className="relative mb-2" ref={guestLinkDropdownRef}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowGuestLinkDropdown(!showGuestLinkDropdown);
                            }}
                            className="w-full p-2 bg-dark-bg border border-dark-border/30 rounded-xl text-sm text-left text-dark-text hover:border-purple-500 focus:outline-none focus:border-purple-500 flex items-center justify-between"
                          >
                            <span className="flex items-center gap-2">
                              <Link2 className="w-4 h-4 text-green-400" />
                              {loadingShareLinks ? 'Loading links...' : `Select from ${shareLinks.length} existing link${shareLinks.length !== 1 ? 's' : ''}`}
                            </span>
                            <span className="text-dark-muted">▼</span>
                          </button>
                          {showGuestLinkDropdown && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 max-h-48 overflow-y-auto">
                              {shareLinks.map((link) => (
                                <button
                                  key={link.id}
                                  type="button"
                                  onClick={() => handleSelectGuestLink(link)}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-purple-500/20 transition-all duration-150 flex flex-col gap-0.5"
                                >
                                  <span className="text-white font-medium truncate">
                                    {link.label || `Link #${link.id}`}
                                  </span>
                                  <span className="text-xs text-dark-muted truncate">
                                    {link.event_count ? `${link.event_count} events` : 'All events'} • {link.share_url.slice(-20)}...
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <input
                        type="url"
                        value={guestLinkUrl}
                        onChange={(e) => setGuestLinkUrl(e.target.value)}
                        placeholder="https://eroland.me/hmc/guest/..."
                        className="w-full p-2 bg-dark-bg border border-dark-border/30 rounded-xl text-sm text-white placeholder:text-dark-muted focus:outline-none focus:border-purple-500"
                      />
                      <div className="mt-1 flex items-center justify-between">
                        <label className="flex items-center gap-2 text-xs text-dark-muted cursor-pointer hover:text-dark-text">
                          <input
                            type="checkbox"
                            checked={saveGuestLinkAsDefault}
                            onChange={(e) => setSaveGuestLinkAsDefault(e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-dark-border/30 bg-dark-bg text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                          />
                          Save as default
                        </label>
                        <span className="text-xs text-dark-muted">
                          {shareLinks.length > 0 ? 'Or select existing link above' : 'Get this from Share → Guest Link'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Test Mode Toggle */}
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={testMode}
                    onChange={(e) => setTestMode(e.target.checked)}
                    className="w-4 h-4 rounded border-dark-border/30 bg-dark-bg text-yellow-500 focus:ring-yellow-500 focus:ring-offset-0"
                  />
                  <span className="text-sm font-medium text-yellow-400">🧪 Test Mode</span>
                  <span className="text-xs text-dark-muted">
                    Pre-fills: e.roland88@gmail.com, Demi Lovato & Meghan Trainor, guest link enabled
                  </span>
                </label>
              </div>

              {/* Send Email Section */}
              <div className="p-4 bg-blue-500/10 border border-brand-500/30 rounded-lg">
                <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Send className="w-4 h-4 text-blue-400" />
                  Send Email Directly
                  <span className="text-xs text-dark-muted font-normal">(via tickets@eroland.me)</span>
                </h3>

                <div className="space-y-3">
                  {/* Email Subject */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-dark-muted">Subject Line</label>
                      <TemplateSelector
                        templates={subjectTemplates}
                        currentValue={emailSubject}
                        onSelect={setEmailSubject}
                        onSave={saveSubjectTemplate}
                        onDelete={deleteSubjectTemplate}
                        placeholder="Choose subject..."
                        compact
                      />
                    </div>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Event Invitation: ..."
                      className="w-full p-2 bg-dark-bg border border-dark-border/30 rounded-xl text-sm text-white placeholder:text-dark-muted focus:outline-none focus:border-brand-500"
                    />
                    <label className="flex items-center gap-2 mt-1 text-xs text-dark-muted cursor-pointer hover:text-dark-text">
                      <input
                        type="checkbox"
                        checked={saveSubjectAsDefault}
                        onChange={(e) => setSaveSubjectAsDefault(e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-dark-border/30 bg-dark-bg text-blue-500 focus:ring-brand-500 focus:ring-offset-0"
                      />
                      Save as default
                    </label>
                  </div>

                  {/* Recipient Email */}
                  <div className="relative">
                    <label className="block text-xs text-dark-muted mb-1">Send To</label>
                    <input
                      ref={emailInputRef}
                      type="email"
                      value={sendToEmail}
                      onChange={(e) => handleEmailInputChange(e.target.value)}
                      onFocus={() => {
                        if (recentEmails.length > 0) {
                          setFilteredEmails(
                            sendToEmail.trim()
                              ? recentEmails.filter((email) =>
                                  email.toLowerCase().includes(sendToEmail.toLowerCase())
                                )
                              : recentEmails
                          );
                          setShowEmailSuggestions(true);
                        }
                      }}
                      placeholder="recipient@example.com"
                      className="w-full p-2 bg-dark-bg border border-dark-border/30 rounded-xl text-sm text-white placeholder:text-dark-muted focus:outline-none focus:border-brand-500"
                    />
                    {/* Email Suggestions Dropdown */}
                    {showEmailSuggestions && filteredEmails.length > 0 && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 max-h-40 overflow-y-auto">
                        {filteredEmails.map((email, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleSelectEmailSuggestion(email)}
                            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-brand-500/20 transition-all duration-150 truncate"
                          >
                            {email}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Send Button */}
                  <button
                    onClick={handleSendEmail}
                    disabled={!canSendEmail || isSending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="email-template-send-button"
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Email
                      </>
                    )}
                  </button>

                  {/* Status Messages */}
                  {sendStatus === 'success' && (
                    <div className="flex items-center gap-2 p-2 bg-green-500/20 border border-green-500/30 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-green-300">Email sent successfully!</span>
                    </div>
                  )}
                  {sendStatus === 'error' && (
                    <div className="flex items-center gap-2 p-2 bg-red-500/20 border border-red-500/30 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <span className="text-sm text-red-300">{sendError}</span>
                    </div>
                  )}

                  <p className="text-xs text-blue-400/70">
                    The email will be sent from tickets@eroland.me with the generated template.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Preview section - Desktop only (side-by-side) */}
          {!isMobile && showPreview && (
            <div className="flex-1 flex flex-col bg-dark-input">
              <div className="p-3 bg-dark-surface/50 border-b border-dark-border/30 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-dark-text">Email Preview</span>
                  {/* Preview Mode Toggle */}
                  <div className="flex gap-1 ml-3 bg-gray-300 rounded-lg p-0.5">
                    <button
                      onClick={() => setPreviewMode('desktop')}
                      className={`p-1.5 rounded transition-all duration-150 ${
                        previewMode === 'desktop'
                          ? 'bg-dark-surface text-dark-text shadow-sm'
                          : 'text-dark-muted hover:text-dark-text'
                      }`}
                      title="Desktop preview (600px)"
                      data-testid="email-template-desktop-preview"
                    >
                      <Monitor className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPreviewMode('mobile')}
                      className={`p-1.5 rounded transition-all duration-150 ${
                        previewMode === 'mobile'
                          ? 'bg-dark-surface text-dark-text shadow-sm'
                          : 'text-dark-muted hover:text-dark-text'
                      }`}
                      title="Mobile preview (375px)"
                      data-testid="email-template-mobile-preview"
                    >
                      <Smartphone className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <span className="text-xs text-dark-muted">
                  {previewMode === 'desktop' ? '600px desktop' : '375px mobile'}
                </span>
              </div>
              <div className="flex-1 overflow-auto p-4 flex justify-center">
                {previewBlobUrl ? (
                  <iframe
                    src={previewBlobUrl}
                    title="Email Preview"
                    className={`h-full min-h-[500px] bg-dark-surface border border-dark-border/30 rounded shadow-sm transition-all duration-200 ${
                      previewMode === 'mobile' ? 'border-4 border-gray-400 rounded-2xl' : ''
                    }`}
                    style={{
                      width: previewMode === 'desktop' ? '600px' : '375px',
                      margin: '0 auto',
                      display: 'block',
                    }}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-dark-muted">
                    <p>Select at least one event to preview</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Mobile Fullscreen Preview Overlay - z-[100] to be above AppShell's PhaseSelector z-50 */}
        {isMobile && mobilePreviewOpen && wizardStep === 1 && (
          <div className="fixed inset-0 z-[100] bg-dark-surface">
            {/* Floating close button - always visible in top right */}
            <button
              onClick={() => setMobilePreviewOpen(false)}
              className="fixed top-4 right-4 z-[110] w-11 h-11 bg-dark-bg/80 backdrop-blur-sm rounded-full flex items-center justify-center text-white shadow-lg hover:bg-dark-surface active:bg-dark-input transition-all duration-150"
              style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
              aria-label="Close preview"
            >
              <X className="w-6 h-6" />
            </button>
            {/* Mobile Preview Content - full screen iframe */}
            {/* Using srcdoc instead of blob URL for better iOS Safari compatibility */}
            <div className="w-full h-full overflow-auto">
              {generatedHtml ? (
                <iframe
                  srcDoc={generatedHtml}
                  title="Email Preview"
                  className="w-full h-full min-h-[calc(100vh-60px)] bg-dark-surface"
                  style={{ border: 'none' }}
                  sandbox="allow-same-origin"
                  onLoad={(e) => {
                    const iframe = e.target as HTMLIFrameElement;
                    try {
                      const doc = iframe.contentDocument;
                      if (doc) {
                        console.log('[EmailPreview] Mobile iframe loaded successfully (srcdoc)');
                        console.log('[EmailPreview] iframe body innerHTML length:', doc.body?.innerHTML?.length || 0);
                        // Check for gradient backgrounds
                        const tables = doc.querySelectorAll('table');
                        tables.forEach((t, i) => {
                          const style = window.getComputedStyle(t);
                          if (style.backgroundImage.includes('gradient')) {
                            console.log(`[EmailPreview] Table ${i} has gradient:`, style.backgroundImage.substring(0, 80));
                          }
                        });
                      } else {
                        console.warn('[EmailPreview] Mobile iframe contentDocument is null - possible cross-origin issue');
                      }
                    } catch (err) {
                      console.error('[EmailPreview] Mobile iframe access error:', err);
                    }
                  }}
                  onError={(e) => {
                    console.error('[EmailPreview] Mobile iframe load error:', e);
                  }}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-dark-muted p-8">
                  <p className="text-center">Select at least one event to preview</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer - only shown on Step 1 */}
        {wizardStep === 1 && (
        <div className={`px-4 md:px-6 py-4 border-t border-dark-border/30 flex items-center justify-between shrink-0 ${isMobile ? 'flex-col gap-3' : ''}`}>
          {/* Preview toggle - different behavior on mobile vs desktop */}
          {isMobile ? (
            <button
              onClick={() => setMobilePreviewOpen(true)}
              disabled={!isFormValid}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-dark-input border border-dark-border/30 rounded-xl text-sm text-white font-medium hover:bg-dark-surface/40 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="email-template-preview-button"
            >
              <Eye className="w-4 h-4" />
              Preview Email
            </button>
          ) : (
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 px-4 py-2 bg-dark-bg border border-dark-border/30 rounded-xl text-sm text-dark-text hover:text-white hover:border-dark-border/30 transition-all duration-150"
              data-testid="email-template-toggle-preview-button"
            >
              {showPreview ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  Hide Preview
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Show Preview
                </>
              )}
            </button>
          )}

          <div className={`flex items-center gap-3 ${isMobile ? 'w-full' : ''}`}>
            {!isMobile && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-dark-muted hover:text-white transition-all duration-150"
                data-testid="email-template-cancel-button"
              >
                Cancel
              </button>
            )}
            {/* View Recipients button for completed invites */}
            {activeInvite?.isCompleted && (
              <button
                onClick={() => setWizardStep(5)}
                className={`flex items-center justify-center gap-2 px-5 py-2 bg-green-500/20 border border-green-500/50 text-green-400 rounded-lg text-sm font-medium hover:bg-green-500/30 transition-all ${isMobile ? 'flex-1 py-3' : ''}`}
                data-testid="email-template-view-recipients-button"
              >
                <CheckCircle2 className="w-4 h-4" />
                {isMobile ? 'View Sent' : 'View Recipients'}
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setWizardStep(2)}
              disabled={!isFormValid}
              className={`flex items-center justify-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg text-sm font-medium hover:from-blue-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isMobile ? 'flex-1 py-3' : ''}`}
              data-testid="email-template-select-recipients-button"
            >
              <Users className="w-4 h-4" />
              {isMobile ? 'Next' : 'Select Recipients'}
              <ChevronRight className="w-4 h-4" />
            </button>
            {!isMobile && (
              <button
                onClick={handleDownload}
                disabled={!isFormValid}
                className="flex items-center gap-2 px-5 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="email-template-download-button"
              >
                <Download className="w-4 h-4" />
                Download .html
              </button>
            )}
            {/* Export History Dropdown - shows when invite has export history */}
            {activeInvite && activeInvite.exportHistory.length > 0 && !isMobile && (
              <ExportHistoryDropdown entries={activeInvite.exportHistory} />
            )}
          </div>
        </div>
        )}
      </div>

      {/* Pipedrive Activity Modal - triggered from Guest List Preview */}
      <CreateActivityModal
        isOpen={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        eventType="hmc"
        defaultDescription={`Invitation Sent - ${selectedEventsArray.map(e => getCleanEventName(e.opponent)).join(', ')}`}
        recipients={selectedWizardContacts
          .filter(c => !excludedPersonIds.has(c.person_id))
          .map(c => ({
            personId: c.person_id,
            name: c.name || '',
            email: getContactPrimaryEmail(c) || '',
            organization: c.organization_name || c.organization || '',
            orgId: c.organization_id ?? undefined,
          }))}
      />
    </div>
  );
};

export default EmailTemplateModal;
